/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  useTheme,
  Tooltip,
  Popover,
  MenuItem,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  TextField,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useParams, useMatch } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { m, AnimatePresence } from 'framer-motion';
import {
  closeWhatsappConversationWithReason,
  fetchMoreWhastappMessages,
  fetchWhatsappChatAndOtherMetaData,
  markAsUnRead,
  pinMessage,
  removeWhatsappTag,
  reOpenWhatsappConversationWithReason,
  resetWhatsappChat,
  saveWhatsappChatMetaData,
  sendWhatsappMessage,
  sendWhatsappMediaMessage,
  generateAiResponse,
  clearAiAssistant,
  fetchAIWizardStatus,
  clearServiceWindowClosedDialog,
  reactToMessage,
  revokeMessage,
  starMessage,
  unstarMessage,
  editMessage,
  forwardMessages,
} from 'src/redux/slices/whatsappV2';
import { updateError } from 'src/redux/slices/user';
import { PATH_DASHBOARD } from 'src/routes/paths';
import {
  matchesSearch,
  formatTemplateName,
  getTemplateBody,
} from 'src/components/whatsappComponents/whatsAppTemplateUtils';
import FeatureGuard from 'src/guards/FeatureGuard';
import useMessageLongPress from 'src/hooks/useMessageLongPress';
import useScrollToMessage from 'src/hooks/useScrollToMessage';
import useComposerAutocomplete from 'src/hooks/useComposerAutocomplete';
import useThreadDraft, { readDraftForThread } from 'src/hooks/useThreadDraft';
import {
  arePhoneNumsEqual,
  formatWindowRemaining,
  isWithin24Hours,
  parsedDateTimeForWhatsapp,
  parseWhatsappMarkup,
  resolveServiceWindow,
  stringAvatar,
} from './whatsappUtil';
import { getStyles, getAvatarColor, COLORS } from './styles';
import SearchLeadForWhatsapp from './SearchLeadForWhatsapp';
import StatusIcon from './WhatsappStatusIcons';
import MediaDisplayComponent from './MediaDisplayComponent';
import AddMediaButtons from './AddMediaButtons';
import QuickActions from './QuickActions';
import WhatsappSkeleton from './WhatsappChatSkeletons';
import WhatsappTemplateModal from './WhatsappTemplatePreview';
import CloseChatPopup from './CloseChatPopup';
import { AIAssistantButton, AIAssistantMenu, AIResultPanel } from './AIAssistant';
import EmojiPickerButton from './EmojiPickerButton';
import FormattingToolbar from './FormattingToolbar';
import MessageActionSheet from './MessageActionSheet';
import EmojiReactionBar from './EmojiReactionBar';
import StarredMessagesPanel from './StarredMessagesPanel';
import PinnedMessageBanner from './PinnedMessageBanner';
import MessageInfoDrawer from './MessageInfoDrawer';
import MultiSelectTopBar from './MultiSelectTopBar';
import ForwardPickerDialog from './ForwardPickerDialog';
import ComposerAutocompletePopover from './ComposerAutocompletePopover';
import { searchEmojiShortcodes } from './emojiShortcodes';
import UnreadDivider from './UnreadDivider';

// Helper to parse date using existing utility (handles DD-MM-YYYY format)
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Use existing parsedDateTimeForWhatsapp with returnRawDateTime=true to get MM-DD-YYYY format
    const parsedStr = parsedDateTimeForWhatsapp(dateStr, null, true);
    if (!parsedStr) return null;
    // Clean microseconds before parsing
    const cleanDate = parsedStr.replace(/\.\d{6}Z?$/, '');
    const date = new Date(cleanDate);
    if (!Number.isNaN(date.getTime())) return date;
    return null;
  } catch (error) {
    return null;
  }
};

// Helper to format date for dividers — native-WA style: Today, Yesterday,
// or absolute "12 May 2026" (falls back to full date if older than a week
// to avoid ambiguity around "Monday"-style weekday labels across locales).
const formatDateForDivider = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return null;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMsg = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfMsg) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  // Absolute date — include year only if different from current year
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(
    'en-GB',
    sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }
  );
};

// Helper to get just the date part for comparison
const getDateKey = (dateStr) => {
  const date = parseDate(dateStr);
  if (!date) return null;
  return date.toDateString();
};

const ChatPage = ({
  onToggleDetails,
  detailsOpen,
  embedded,
  // eslint-disable-next-line no-unused-vars
  onOpenBooking,
  onSetChatMessage,
  onOpenResolve,
  hideResolve,
  onMessageSent,
  isMobile,
  isCompact,
  onBack,
  onToggleFullscreen,
  isFullscreen,
  onToggleFocusMode,
  isFocusMode,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme, { isMobile });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { practiceId } = useParams();
  // Direct-load deep links — nested route param `threadId` from
  // /dashboard/practice/:practiceId/whatsapp/thread/:threadId. `useParams`
  // alone returns only the parent route's params; `useMatch` is the
  // canonical way to read nested params here. Used by the URL → thread
  // backup-fetch effect below.
  const threadRouteMatch = useMatch('/dashboard/practice/:practiceId/whatsapp/thread/:threadId');
  const urlThreadId = threadRouteMatch?.params?.threadId ?? null;
  const [whatsappMessages, setWhatsappMessages] = React.useState([]);
  const [templateModalOpen, setTemplateModalOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [sendMessage, setSendMessage] = React.useState('');
  // Expose setSendMessage to parent for pre-filling chat input
  React.useEffect(() => {
    if (onSetChatMessage) onSetChatMessage(setSendMessage);
  }, [onSetChatMessage]);
  const [isUserNearBottom, setIsUserNearBottom] = React.useState(true);
  // Count of messages that arrived while the user was scrolled up — surfaced
  // on the scroll-to-bottom FAB as a small green badge. Resets to 0 when the
  // user scrolls back to the bottom or clicks the FAB.
  const [unreadWhileScrolled, setUnreadWhileScrolled] = React.useState(0);
  // Bubble action sheet — anchor carries { anchorEl, msg }. Driven by
  // useMessageLongPress so right-click (desktop), long-press (touch),
  // and shift+click (keyboard) all funnel through one activation path.
  const [bubbleMenu, setBubbleMenu] = React.useState(null);
  // Delete-for-everyone confirmation. When set, holds the message we're
  // about to revoke so the dialog can render against a closed action sheet.
  const [revokeTarget, setRevokeTarget] = React.useState(null);
  // Track B — Star/Pin UI state.
  const [starredPanelOpen, setStarredPanelOpen] = React.useState(false);
  const [replacePinTarget, setReplacePinTarget] = React.useState(null);
  // Track C / F6 inline edit. Shape: { wamid, draft } | null.
  // We key off wamid (Meta's stable id, present on both optimistic and
  // server-confirmed messages once Meta acks the send) instead of
  // msg.id, because the optimistic-send path uses Date.now() as a
  // placeholder id and the slice replaces it with the real DB PK on
  // confirmation — keying off id meant the inline TextField stopped
  // rendering the moment the server response landed. wamid survives
  // that swap. Edit is gated on wamid presence (un-acked optimistic
  // messages have no wamid yet — and the BE rejects edit on those).
  const [editingBubble, setEditingBubble] = React.useState(null);
  // Track C / F8 message info — { messageId, threadId } | null.
  const [messageInfoTarget, setMessageInfoTarget] = React.useState(null);
  // Reply-to-quote: when set, the composer renders a quote preview card
  // above the textarea and the next outbound send carries `replied_to_wamid`
  // so Meta posts the message as a threaded reply on the patient's device.
  // Shape: { wamid, body, fromWaid, isFromMe } | null.
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [isWindowExpired, setIsWindowExpired] = React.useState(false);
  // Re-render the window pill once a minute so the "5h 12m" countdown
  // ticks down without needing the operator to re-open the thread.
  const [, setWindowTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setWindowTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [templateDropdownOpen, setTemplateDropdownOpen] = React.useState(false);
  const [templatePreview, setTemplatePreview] = React.useState(null);
  const [openCloseConversation, setOpenCloseConversation] = React.useState(false);
  // closeReason state removed — managed inside CloseChatPopup now
  const [quickCloseAnchor, setQuickCloseAnchor] = React.useState(null);
  const [aiMenuOpen, setAiMenuOpen] = React.useState(false);
  const [aiMenuAnchor, setAiMenuAnchor] = React.useState(null);
  const [lastAiAction, setLastAiAction] = React.useState(null);
  const [lastAiContext, setLastAiContext] = React.useState(null);
  const [expandedTemplates, setExpandedTemplates] = React.useState(new Set());
  const [attachmentFile, setAttachmentFile] = React.useState(null);
  const [attachmentPreview, setAttachmentPreview] = React.useState(null);
  const [attachmentCaption, setAttachmentCaption] = React.useState('');
  // Attachment "+" popover — native-WA plus-button grid. Anchor drives open/close.
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = React.useState(null);
  // Tier 2 / F-16 — drag-and-drop overlay.
  // We track drag-enter / drag-leave with a counter rather than a
  // boolean because dragenter/dragleave fire for every child element
  // the cursor crosses; a naive boolean toggles off as soon as the
  // cursor enters a nested div.
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragCounterRef = React.useRef(0);
  // Tier 2 / F-16 — multi-file drop queue. We process drops one at
  // a time through the existing single-file send path so the
  // operator can review caption + cancel each one.
  const [pendingDropQueue, setPendingDropQueue] = React.useState([]);
  const inputRef = React.useRef(null);
  const scrollRef = React.useRef();
  const fileInputRef = React.useRef(null);
  const quickActionsRef = React.useRef({});
  const attachmentPreviewRef = React.useRef(null);
  // Tracks message IDs that were present at initial render of the current
  // chat. Any received message whose id is NOT in this set when rendered
  // is treated as "newly arrived" and gets the subtle slide-in animation.
  // Reset whenever the chat thread identity changes.
  const seenMessageIdsRef = React.useRef(new Set());
  const seededForThreadRef = React.useRef(null);
  // Tier 3 / F-33 — first-unread divider.
  // Snapshot the unread count when the thread is FIRST opened so the
  // divider's position doesn't shift after `mark_as_read` zeroes the
  // server-side counter. The snapshot resets whenever the thread
  // identity changes (re-open / refresh) — matching the spec
  // "divider stays visible until thread is reopened/refreshed".
  const unreadSnapshotForThreadRef = React.useRef({ threadId: null, count: 0 });
  const [unreadDividerCount, setUnreadDividerCount] = React.useState(0);
  // True when the unread divider element is currently inside the
  // scrollable viewport — drives the floating "N unread ↓" jump button
  // visibility. Defaults to false (show button) until the IO observer
  // reports a state.
  const [isUnreadDividerVisible, setIsUnreadDividerVisible] = React.useState(false);
  const unreadDividerRef = React.useRef(null);
  // --- Swipe-to-reply gesture state (mobile only) -----------------------
  // Tracks the in-flight drag for a single bubble. We bypass React state
  // during the drag itself — onTouchMove fires up to 60×/s and re-rendering
  // the entire chat list each frame would jank. Instead we mutate the
  // dragged bubble's wrapper transform directly via DOM and only commit
  // the reply via setReplyingTo once the gesture completes past threshold.
  // Shape:
  //   { id, startX, startY, deltaX, wrapperEl, indicatorEl, msg, cancelled }
  // null when no drag is active.
  const swipeStateRef = React.useRef(null);

  const {
    isLoading,
    whatsAppChatMetaData,
    whatsappChats,
    whatsAppChatNextAPI,
    whatsappChatScreenLoading,
    whatsappChatNextAPILoading,
    whatsppSendLoading,
    currentWhatsappTemplate,
    allowedUsageTypes,
    aiAssistantLoading,
    aiAssistantResult,
    aiAssistantError,
    aiAssistantActionType,
    practiceChoices,
    serviceWindowClosedDialog,
    currentBusinessId,
    currentWhatsappPhoneId,
  } = useSelector(
    (state) => ({
      isLoading: state?.whatsappV2?.isLoading,
      whatsAppChatMetaData: state?.whatsappV2?.whatsAppChatMetaData,
      whatsappChats: state?.whatsappV2?.whatsappChats,
      whatsAppChatNextAPI: state?.whatsappV2?.whatsAppChatNextAPI,
      whatsappChatScreenLoading: state?.whatsappV2?.whatsappChatScreenLoading,
      whatsappChatNextAPILoading: state?.whatsappV2?.whatsappChatNextAPILoading,
      whatsppSendLoading: state?.whatsappV2?.whatsppSendLoading,
      currentWhatsappTemplate: state?.whatsappV2?.currentWhatsappTemplate,
      allowedUsageTypes: state?.whatsappV2?.allowedUsageTypes,
      aiAssistantLoading: state?.whatsappV2?.aiAssistantLoading,
      aiAssistantResult: state?.whatsappV2?.aiAssistantResult,
      aiAssistantError: state?.whatsappV2?.aiAssistantError,
      aiAssistantActionType: state?.whatsappV2?.aiAssistantActionType,
      practiceChoices: state?.whatsappV2?.practiceChoices,
      serviceWindowClosedDialog: state?.whatsappV2?.serviceWindowClosedDialog,
      currentBusinessId: state?.whatsappV2?.currentBusinessId,
      currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
    }),
    shallowEqual
  );

  // Filter templates by allowed usage types from practice choices config
  const filteredTemplates = React.useMemo(() => {
    if (!allowedUsageTypes || allowedUsageTypes.length === 0) return currentWhatsappTemplate;
    return (currentWhatsappTemplate || []).filter((t) => allowedUsageTypes.includes(t.usage_type));
  }, [currentWhatsappTemplate, allowedUsageTypes]);

  // Check if AI is enabled — reads from Redux directly
  const aiWizardConfig = useSelector((state) => state?.whatsappV2?.aiWizardConfig);
  const isAiEnabled = Boolean(aiWizardConfig && aiWizardConfig.enableAiForWhatsapp !== false);

  // Tier 2 / F-22 — merge tags. The dashboard layout pre-fetches the
  // global tag list on mount, but `/api/v1/utils/mergetags/` can fail
  // (network blip, 4xx for some users) leaving Redux state at the
  // initial empty array — in which case '@' would otherwise produce
  // zero suggestions. Treat the hardcoded list as the always-available
  // baseline and merge any real fetched tags on top, so operators get
  // a useful popover regardless of whether the fetch succeeded.
  const mergeTagKeys = useSelector((state) => state?.mergeTags?.mergeTagKeys, shallowEqual);
  const fallbackMergeTags = React.useMemo(
    () => [
      'first_name',
      'last_name',
      'practice',
      'practice_name',
      'lead_id',
      'appointment_date',
      'appointment_time',
      'treatment',
    ],
    []
  );
  const availableMergeTags = React.useMemo(() => {
    const real = Array.isArray(mergeTagKeys) ? mergeTagKeys : [];
    // De-dupe via Set so the baseline keys don't double up if the
    // backend list happens to include them. Sort for stable display
    // order in the autocomplete popover.
    return [...new Set([...fallbackMergeTags, ...real])].sort();
  }, [mergeTagKeys, fallbackMergeTags]);

  // GDPR FIX: Fail-secure check for lead communication status
  // If thread has a linked lead, we MUST have leadDetails loaded before allowing send
  // This prevents bypassing GDPR checks via race conditions during chat load
  const threadHasLead = Boolean(whatsAppChatMetaData?.thread?.lead?.id);
  const leadDetailsLoaded = Boolean(whatsAppChatMetaData?.leadDetails?.id);
  const isLeadOpenForCommunication = whatsAppChatMetaData?.leadDetails?.is_open_for_communication !== false;

  // Fail-secure: Disable send if thread has lead but leadDetails isn't loaded yet
  const isSendAllowed = threadHasLead ? leadDetailsLoaded && isLeadOpenForCommunication : true;

  // 24-hour Meta service window — server-aware. The thread serializer
  // exposes ``service_window_open`` and ``service_window_closes_at``;
  // ``resolveServiceWindow`` falls back to the legacy client-side check
  // off the last received bubble's timestamp if those fields aren't
  // present (older API response). Re-evaluated whenever the thread or
  // the minute-tick changes so the "5h 12m" countdown stays current.
  const serviceWindow = React.useMemo(
    () => resolveServiceWindow(whatsAppChatMetaData?.thread),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      whatsAppChatMetaData?.thread?.service_window_open,
      whatsAppChatMetaData?.thread?.service_window_closes_at,
      whatsAppChatMetaData?.thread?.last_inbound_at,
    ]
  );

  // Tier 2 / F-18 — per-thread draft persistence (localStorage).
  // When the thread changes, we hydrate the composer from the saved
  // draft for that thread (or clear it if none). Subsequent edits
  // are pushed back into the hook on every change so the next
  // thread switch / refresh restores them. The hook itself debounces
  // localStorage writes so we don't thrash on every keystroke.
  const threadIdForDraft = whatsAppChatMetaData?.thread?.id ?? null;
  const { setDraft: persistDraft, clearDraft: clearPersistedDraft } = useThreadDraft(threadIdForDraft);
  const lastHydratedThreadRef = React.useRef(undefined);
  React.useEffect(() => {
    // Only re-hydrate when the thread identity actually changes.
    // Otherwise every keystroke would race the debounced save and
    // overwrite live typing with stale storage state.
    //
    // Read directly from localStorage (not the hook's `draft` state)
    // because the hook's re-hydration useEffect is queued to run
    // AFTER this parent effect — reading the hook's state here would
    // see the previous thread's draft value. The exported helper
    // gives us the freshly-stored value synchronously.
    if (lastHydratedThreadRef.current !== threadIdForDraft) {
      lastHydratedThreadRef.current = threadIdForDraft;
      setSendMessage(readDraftForThread(threadIdForDraft) || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadIdForDraft]);

  // Tier 2 / F-20, F-21, F-22 — composer autocomplete state.
  const composerAutocomplete = useComposerAutocomplete();

  React.useEffect(() => {
    // Don't process until we have actual data loaded (not during loading state)
    if (whatsappChatScreenLoading || whatsappChats === undefined || whatsappChats === null) {
      setWhatsappMessages([]);
      setIsWindowExpired(false);
      return;
    }

    setIsUserNearBottom(true);
    const lastRecievedMessage = {};
    const messages = whatsappChats?.flatMap((message, index) => {
      const content = message?.body;
      const type = arePhoneNumsEqual(message?.from_waid, whatsAppChatMetaData?.phoneNumber) ? 'received' : 'sent';
      const id = message?.id;
      const timestamp = parsedDateTimeForWhatsapp(message?.created_at, message?.created_at_human);
      const updatedTimestamp = parsedDateTimeForWhatsapp(message?.updated_at, message?.created_at_human);
      const isMessageInteractive = message?.message_type === 'interactive';
      const flowResponseEmail = message?.flow_response?.email;
      const flowResponseTreatment = message?.flow_response?.treatment;
      const flowResponseComment = message?.flow_response?.comment;
      const isSpecialTemplateMessage =
        message?.message_type === 'template' &&
        message?.template?.name?.length &&
        !message?.template?.components?.[0]?.example;
      const emoji = message?.whatsapp_message_reaction?.emoji || null;
      const timerTime = parsedDateTimeForWhatsapp(message?.created_at, '', true);
      const status = message?.status;
      const mediaMessage = message?.whatsapp_message_media;
      const createdAt = message?.created_at;

      if (type === 'received' && !lastRecievedMessage?.id) {
        lastRecievedMessage.time = timerTime;
        lastRecievedMessage.createdAt = createdAt;
        lastRecievedMessage.id = true;
      }
      // Reply-to-quote: surface the quoted message as a small card we
      // render INSIDE the bubble. The backend serializer hands us a
      // tiny block { id, wamid, body, message_type, from_waid }; the
      // frontend infers ``isFromMe`` by comparing from_waid against the
      // patient's wa_id (same as the bubble-side type detection above).
      let repliedTo = null;
      if (message?.replied_to) {
        const r = message.replied_to;
        repliedTo = {
          id: r?.id ?? null,
          wamid: r?.wamid ?? null,
          body: r?.body || '',
          messageType: r?.message_type || 'text',
          fromWaid: r?.from_waid || '',
          // True when the quoted message was sent BY us (the practice).
          // Mirrors how ``type`` is derived for the parent loop above.
          isFromMe: !arePhoneNumsEqual(r?.from_waid, whatsAppChatMetaData?.phoneNumber),
        };
      }
      const returnObj = {
        id,
        wamid: message?.wamid || null,
        fromWaid: message?.from_waid || null,
        timestamp,
        isMessageInteractive,
        flowResponseEmail,
        flowResponseComment,
        flowResponseTreatment,
        isSpecialTemplateMessage,
        emoji,
        timerTime,
        content: message?.is_revoked ? null : content,
        type,
        updatedTimestamp,
        status,
        mediaMessage,
        mediaMessageType: message?.message_type,
        createdAt,
        isEdited: message?.is_edited || false,
        isRevoked: message?.is_revoked || false,
        isStarred: message?.is_starred || false,
        // Track D / W-1 — forward provenance. BE serializer emits
        // `forwarded_from` for any message Meta (or our own
        // forwardMessages thunk) marked as a forward. Falsy when
        // the message is original.
        forwardedFrom: message?.forwarded_from ?? null,
        repliedTo,
      };
      if (index === 0 && type === 'received' && !mediaMessage) return returnObj;
      if (index === 0 && type === 'received' && mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      if (isSpecialTemplateMessage && mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      if (mediaMessage)
        return [
          {
            id: `System${message?.id}media`,
            type: 'system',
            content: '',
            timerTime,
            specialType: 'addMediaButtons',
          },
          returnObj,
        ];
      return returnObj;
    });

    let windowExpired = false;
    if (messages?.length === 0) {
      messages.push({
        id: `System${Date.now()}`,
        type: 'system',
        content: 'Please select a template to start a conversation.',
        freeze: true,
      });
      windowExpired = true;
    } else if (!lastRecievedMessage?.id) {
      messages?.unshift({
        id: `System${Date.now()}`,
        type: 'system',
        content: 'Please select a template to continue the conversation.',
        freeze: true,
      });
      windowExpired = true;
    } else if (lastRecievedMessage?.id && !isWithin24Hours(lastRecievedMessage?.time)) {
      messages?.unshift({
        id: `System${Date.now()}windowAlert`,
        type: 'windowAlert',
        startTime: lastRecievedMessage?.createdAt,
      });
      messages?.unshift({
        id: `System${Date.now()}templateRequired`,
        type: 'templateRequired',
      });
      windowExpired = true;
    }

    // Server-aware override: if the backend exposes ``service_window_open``
    // (added by the WhatsApp PMP work), trust it over the client-side
    // estimate. A server-closed window means Meta will refuse free-form
    // delivery — block the input regardless of what the visible bubble
    // timestamps suggest.
    if (
      whatsAppChatMetaData?.thread &&
      typeof whatsAppChatMetaData.thread.service_window_open === 'boolean' &&
      !whatsAppChatMetaData.thread.service_window_open
    ) {
      windowExpired = true;
    }

    setIsWindowExpired(windowExpired);
    setWhatsappMessages(messages);

    // Seed the "already seen" id set on the first population for the
    // current thread so the initial render doesn't animate every
    // message — only subsequent arrivals do. Subsequent effect runs
    // for the same thread leave newly-arrived ids unseen until the
    // post-render commit effect marks them.
    const threadKey = whatsAppChatMetaData?.phoneNumber || whatsAppChatMetaData?.thread?.id || null;
    if (threadKey && seededForThreadRef.current !== threadKey) {
      seenMessageIdsRef.current = new Set((messages || []).map((m) => m?.id).filter(Boolean));
      seededForThreadRef.current = threadKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    whatsAppChatMetaData?.phoneNumber,
    whatsAppChatMetaData?.thread?.id,
    whatsAppChatMetaData?.thread?.service_window_open,
    whatsAppChatMetaData?.thread?.service_window_closes_at,
    whatsappChats,
    whatsappChatScreenLoading,
  ]);

  // After each render, mark every currently-rendered message id as seen
  // so the next render cycle only animates truly-new arrivals.
  React.useEffect(() => {
    (whatsappMessages || []).forEach((m) => {
      if (m?.id != null) seenMessageIdsRef.current.add(m.id);
    });
  }, [whatsappMessages]);

  // Tier 3 / F-33 — snapshot unread count on thread open.
  // We capture the BE-reported count exactly once per (thread × open),
  // then leave it frozen until the user navigates away and back. This
  // way the divider doesn't disappear the moment our auto-scroll
  // triggers a `mark_as_read` (which zeroes `unread_count` server
  // side). Risk gate: if the field is missing, count stays 0 and the
  // divider doesn't render — fail closed, no crash.
  React.useEffect(() => {
    const threadId = whatsAppChatMetaData?.thread?.id ?? null;
    const reported = whatsAppChatMetaData?.thread?.unread_count;
    if (!threadId) {
      unreadSnapshotForThreadRef.current = { threadId: null, count: 0 };
      setUnreadDividerCount(0);
      setIsUnreadDividerVisible(false);
      return;
    }
    if (unreadSnapshotForThreadRef.current.threadId !== threadId) {
      const safe = typeof reported === 'number' && reported > 0 ? reported : 0;
      unreadSnapshotForThreadRef.current = { threadId, count: safe };
      setUnreadDividerCount(safe);
      // Reset visibility — the IO observer will fire on first render.
      setIsUnreadDividerVisible(false);
    }
    // Snapshot ONLY when the thread identity changes. unread_count is
    // intentionally NOT a dep — fetchWhatsappChatAndOtherMetaData
    // dispatches mark_as_read on open which then refreshes the thread
    // with unread_count=0; if we depended on unread_count we'd re-run
    // and zero the snapshot, vanishing the divider on every re-open
    // of the same thread within the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppChatMetaData?.thread?.id]);

  // Tier 3 / F-33 — track when the divider scrolls into the viewport
  // so we can hide the floating jump button. IntersectionObserver is
  // cheap and avoids tying us to scroll events. The observer is
  // re-attached whenever the divider mounts (i.e. when the count
  // changes from 0 → N).
  React.useEffect(() => {
    const node = unreadDividerRef.current;
    if (!node || unreadDividerCount <= 0) {
      return undefined;
    }
    const root = scrollRef.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsUnreadDividerVisible(entry.isIntersecting);
        });
      },
      { root, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [unreadDividerCount]);

  // Track D / F10 — when ConversationSearchModal opens a thread on a
  // message-body hit it stashes `scrollToMessageId` on the chat
  // metadata. Wait for the message list to load (the message we want
  // must be in `whatsappMessages`), then scroll-and-flash it via the
  // shared useScrollToMessage hook. Once scrolled, clear the flag so
  // we don't repeat on every re-render. The 250ms delay gives MUI's
  // InfiniteScroll layout a chance to settle before we measure the
  // bubble's offset.
  const scrolledForMsgIdRef = React.useRef(null);
  React.useEffect(() => {
    const targetId = whatsAppChatMetaData?.scrollToMessageId;
    if (targetId == null) return undefined;
    if (scrolledForMsgIdRef.current === targetId) return undefined;
    if (!whatsappMessages?.length) return undefined;
    const present = whatsappMessages.some((m) => `${m?.id}` === `${targetId}`);
    if (!present) return undefined;
    const t = setTimeout(() => {
      scrollToMessage(targetId);
      scrolledForMsgIdRef.current = targetId;
      // Clear the scroll-to flag on the metadata so future
      // re-renders don't re-trigger; structuredClone preserves
      // the rest of the metadata shape.
      const cloned = whatsAppChatMetaData ? { ...whatsAppChatMetaData, scrollToMessageId: null } : whatsAppChatMetaData;
      dispatch(saveWhatsappChatMetaData(cloned));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppChatMetaData?.scrollToMessageId, whatsappMessages]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      // Column-reverse container: scrollTop === 0 means pinned to the
      // newest message. Native WA treats anything within ~200 px as
      // "still at the bottom" and auto-follows new arrivals.
      const nearBottom = el.scrollTop > -200;
      if (nearBottom) scrollToBottom();
      else {
        // New message arrived while user is scrolled up — bump the
        // unread counter so the FAB's badge reflects how many they've
        // missed while browsing history.
        setUnreadWhileScrolled((c) => c + 1);
      }
    }
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      const nearBottom = el.scrollTop > -200;
      setIsUserNearBottom(nearBottom);
      // If user scrolls themselves back to the bottom, clear the unread
      // marker — the FAB hides and the next arrival re-starts the count.
      if (nearBottom) {
        setUnreadWhileScrolled(0);
      }
    };
    const timer = setTimeout(() => {
      const el = scrollRef.current;
      if (el) {
        el.addEventListener('scroll', handleScroll);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const el = scrollRef.current;
      if (el) el.removeEventListener('scroll', handleScroll);
    };
  }, [whatsappMessages]);

  const scrollToBottom = () => {
    setIsUserNearBottom(true);
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setUnreadWhileScrolled(0);
    }
  };

  const fetchMoreMessages = () => {
    dispatch(fetchMoreWhastappMessages());
  };

  const handleCloseChat = () => {
    dispatch(saveWhatsappChatMetaData({}));
    // In embedded mode, don't navigate - just clear the chat
    // Navigate to base whatsapp URL to keep URL in sync with closed chat
    if (practiceId && !embedded) {
      navigate(PATH_DASHBOARD.whatsapp.root(practiceId));
    }
  };

  const handleCancelCloseConversation = () => {
    setOpenCloseConversation(false);
  };

  const handleConfirmCloseConversation = (reason) => {
    // CloseChatPopup already completed the ContactRound.
    // This just closes the WhatsApp thread.
    dispatch(
      closeWhatsappConversationWithReason(
        whatsAppChatMetaData?.thread?.id,
        reason,
        whatsAppChatMetaData?.leadDetails?.id
      )
    );
    setOpenCloseConversation(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleQuickCloseClick = (e) => setQuickCloseAnchor(e.currentTarget);
  const handleQuickCloseClose = () => setQuickCloseAnchor(null);
  const handleQuickClose = () => {
    handleQuickCloseClose();
    // Always open the full CloseChatPopup so the ContactRound flow runs.
    // Quick-close without ContactRound would skip the audit trail.
    setOpenCloseConversation(true);
  };

  // --- Bubble action sheet ----------------------------------------------
  // Eligibility shared with the swipe-to-reply gesture below — system,
  // template, revoked or empty bubbles don't expose meaningful actions.
  const isBubbleActionEligible = (msg) => {
    if (!msg || (msg.type !== 'sent' && msg.type !== 'received')) return false;
    if (msg.isRevoked || msg.isSpecialTemplateMessage) return false;
    const hasText = Boolean(msg.content || msg?.mediaMessage?.whatsapp_media_caption);
    const hasMedia = Boolean(msg?.mediaMessage?.whatsapp_media_url);
    return hasText || hasMedia;
  };
  // Track D / F2 multi-select. The longPress hook owns the
  // selection state — we never duplicate it in component state. The
  // action sheet's "Select" button enters multi-select with the current
  // message; once in multi-select, every long-press / tap on a bubble
  // toggles membership instead of opening the sheet.
  const longPressRef = React.useRef(null);
  const openBubbleMenu = React.useCallback((msg, anchorEl) => {
    if (msg?.id == null) return;
    // Already in multi-select? Long-press / right-click toggles membership.
    const lp = longPressRef.current;
    if (lp && lp.isSelecting) {
      if (isBubbleActionEligible(msg)) lp.toggleSelect(msg.id);
      return;
    }
    if (!isBubbleActionEligible(msg) || !anchorEl) return;
    setBubbleMenu({ anchorEl, msg });
  }, []);
  const closeBubbleMenu = () => setBubbleMenu(null);

  // Track C / F6 — edit eligibility. Outbound + text-only + non-revoked +
  // within Meta's 15-minute hard window (we drop the option silently per
  // D8 — no countdown warning). Re-evaluated on every render so the menu
  // item disappears the moment the window expires. The 15-minute literal
  // is hard-coded by Meta; defining it as a module-level constant would
  // make the eslint exhaustive-deps lint happy but at the cost of pulling
  // a magic number out of view from the eligibility check.
  const isBubbleEditEligible = React.useCallback((msg) => {
    const EDIT_WINDOW_MS = 15 * 60 * 1000;
    if (!msg) return false;
    if (msg.type !== 'sent') return false;
    if (msg.isRevoked) return false;
    if (msg.mediaMessageType && msg.mediaMessageType !== 'text') return false;
    // Pre-Meta-ack optimistic bubbles have no wamid — BE rejects edit
    // on those, and our editor identity (C-FE-2) keys off wamid, so
    // surface the constraint up-front. Fail-closed.
    if (!msg.wamid) return false;
    // API serialises createdAt as DD-MM-YYYY HH:mm:ss.ffffffZ (UK
    // day-first). `new Date(...)` on that returns Invalid Date,
    // which previously hid Edit on every server-confirmed message
    // — the bug this fixup is closing. We can't reuse the module
    // parseDate helper here because parsedDateTimeForWhatsapp emits
    // "MM-DD-YYYY HH:mm:ss.ffffffZ", and `new Date()` of a non-ISO
    // string with Z is undefined behaviour across engines (some
    // engines drop the Z and treat it as local time, blowing the
    // 15-minute eligibility window in summer). Build a real ISO
    // string instead: YYYY-MM-DDTHH:mm:ss.ffffffZ. Optimistic
    // sends already emit ISO, so they fall through the regex and
    // hit the native parser unchanged.
    try {
      const raw = msg.createdAt;
      if (!raw) return false;
      const m = typeof raw.match === 'function' ? raw.match(/^(\d{2})-(\d{2})-(\d{4})/) : null;
      const iso = m ? `${m[3]}-${m[2]}-${m[1]}T${raw.slice(11)}` : raw;
      const created = new Date(iso);
      if (Number.isNaN(created.getTime())) return false;
      return Date.now() - created.getTime() < EDIT_WINDOW_MS;
    } catch (_err) {
      // Unparseable timestamp — fail-closed. Better to hide Edit
      // than to expose a 15-minute window we can't enforce.
      return false;
    }
  }, []);

  const handleEditBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    // isBubbleEditEligible already guards on wamid presence, so the
    // setter below can trust msg.wamid is truthy.
    if (!msg || !isBubbleEditEligible(msg)) return;
    setEditingBubble({
      wamid: msg.wamid,
      draft: msg.content || '',
    });
  };

  const cancelEdit = React.useCallback(() => {
    setEditingBubble(null);
  }, []);

  const submitEdit = React.useCallback(() => {
    if (!editingBubble) return;
    const trimmed = (editingBubble.draft || '').trim();
    if (!trimmed) {
      // Empty body would 400 server-side; keep the editor open so the
      // operator can decide whether to cancel or rewrite.
      return;
    }
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) {
      dispatch(updateError({ message: 'Thread not loaded — please retry.', success: false }));
      return;
    }
    // editingBubble keys off wamid (stable across optimistic→confirmed
    // swap). The slice's editMessage thunk takes the DB PK in the URL,
    // so resolve current msg.id by wamid lookup at dispatch time. If
    // the message has rotated out of view (paginated away) we abort —
    // can't edit what we can't optimistically swap.
    const { wamid, draft } = editingBubble;
    const target = (whatsappChats || []).find((m) => m?.wamid && m.wamid === wamid);
    if (!target?.id) {
      setEditingBubble(null);
      dispatch(updateError({ message: 'Message no longer in view — please retry.', success: false }));
      return;
    }
    // Optimistic Save → close the editor immediately. The slice's
    // editMessage thunk handles optimistic body swap + revert on failure;
    // we don't keep the textfield open while waiting because that
    // produces a flash of stale state on success.
    setEditingBubble(null);
    dispatch(editMessage(threadId, target.id, draft));
  }, [editingBubble, whatsAppChatMetaData?.thread?.id, whatsappChats, dispatch]);

  const handleInfoBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    if (!msg) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    setMessageInfoTarget({ messageId: msg.id, threadId });
  };

  const longPress = useMessageLongPress({ onActivate: openBubbleMenu });
  const { isSelecting, selectedIds, toggleSelect, clearSelect } = longPress;
  // Keep a ref so openBubbleMenu sees the latest isSelecting flag
  // without having to reconstruct the callback on every state change.
  React.useEffect(() => {
    longPressRef.current = longPress;
  }, [longPress]);
  const { scrollToMessage } = useScrollToMessage();

  // Track D / F3 — Forward picker dialog open state. Selection lives in
  // the longPress hook; this only tracks dialog visibility + the IDs we
  // captured at open time so the user can keep mutating selection
  // without it changing under the dialog.
  const [forwardPicker, setForwardPicker] = React.useState({ open: false, ids: [] });

  // Strip WhatsApp markup so clipboard gets plain text, not "*bold*" runs.
  const plainBubbleText = (msg) => {
    const raw = msg?.content || msg?.mediaMessage?.whatsapp_media_caption || '';
    if (!raw) return '';
    const html = parseWhatsappMarkup(raw);
    if (typeof html !== 'string') return raw;
    // Parse as DOM and extract textContent — safer than regex for nested tags.
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return (doc.body.textContent || raw).trim();
    } catch (err) {
      return raw;
    }
  };

  const handleCopyBubble = async () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const text = plainBubbleText(msg);
    if (!text) {
      dispatch(updateError({ success: false, message: 'Nothing to copy' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      dispatch(updateError({ success: true, message: 'Copied to clipboard' }));
    } catch (err) {
      dispatch(updateError({ success: false, message: 'Copy failed' }));
    }
  };

  // `msg` is optional — falls back to the right-click menu's selected
  // message so the existing menu wiring keeps working unchanged. Mobile
  // swipe-to-reply passes the message directly without going via the menu.
  const handleReplyBubble = (msg) => {
    const target = msg || bubbleMenu?.msg;
    closeBubbleMenu();
    if (!target) return;
    // The optimistic "sending" placeholder doesn't have a wamid yet — we
    // can't quote it because Meta needs the real wamid in `context`. The
    // operator will need to wait for the bubble to confirm, then reply.
    const wamid = target?.wamid;
    if (!wamid) {
      dispatch(
        updateError({
          success: 'warning',
          message: "This message hasn't been delivered yet — try replying once it confirms.",
        })
      );
      return;
    }
    const text = plainBubbleText(target);
    const fromWaid =
      target.type === 'sent'
        ? whatsAppChatMetaData?.phoneNumber || ''
        : target?.fromWaid || whatsAppChatMetaData?.phoneNumber || '';
    setReplyingTo({
      wamid,
      body: text,
      fromWaid,
      isFromMe: target.type === 'sent',
    });
    // Pop the cursor into the textarea so the operator can type
    // straight away — matches native WA's reply UX.
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // --- Swipe-to-reply (mobile) ------------------------------------------
  // Reuses the same eligibility rule as the bubble action sheet so a
  // bubble that can't open the menu also can't be swiped to reply.
  const isBubbleReplyEligible = isBubbleActionEligible;

  // Threshold + visual-cap constants for the swipe gesture. Matched to
  // native WA on Android — feels responsive on phones, doesn't fight
  // vertical scroll. Both in CSS px.
  const SWIPE_THRESHOLD_PX = 40;
  const SWIPE_MAX_TRANSLATE_PX = 60;
  // Vertical-vs-horizontal disambiguation. We need a small movement
  // budget before deciding direction so a perfectly diagonal flick
  // doesn't immediately get classified as scroll.
  const SWIPE_DIRECTION_LOCK_PX = 6;

  const handleBubbleTouchStart = (event, msg) => {
    if (!isMobile) return;
    if (!isBubbleReplyEligible(msg)) return;
    // Multi-touch (pinch-zoom on a media bubble) — bail.
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    // currentTarget is the bubble Box itself (the element we attached
    // the touch handlers to). Its parentNode is `messageContent` —
    // that's what we translate so the bubble + any meta below it move
    // together. The indicator sits inside the wrapper (one level up
    // from messageContent) and is found via [data-swipe-indicator].
    const bubbleEl = event.currentTarget;
    const contentEl = bubbleEl?.parentNode;
    const wrapperEl = contentEl?.parentNode;
    if (!contentEl || !wrapperEl) return;
    const indicatorEl = wrapperEl.querySelector('[data-swipe-indicator="1"]') || null;
    swipeStateRef.current = {
      id: msg.id,
      startX: touch.clientX,
      startY: touch.clientY,
      deltaX: 0,
      contentEl,
      indicatorEl,
      msg,
      cancelled: false,
      locked: null, // 'horizontal' | 'vertical' | null
    };
  };

  const handleBubbleTouchMove = (event) => {
    const state = swipeStateRef.current;
    if (!state || state.cancelled) return;
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;

    // Direction lock: once we know whether this is a scroll or a swipe,
    // commit and don't second-guess. Vertical wins if |dy| > |dx| * 1.5.
    if (state.locked == null) {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      if (absDx < SWIPE_DIRECTION_LOCK_PX && absDy < SWIPE_DIRECTION_LOCK_PX) {
        return; // wait for a clearer signal
      }
      if (absDy > absDx * 1.5) {
        // User is scrolling — yield to the chat list.
        state.cancelled = true;
        return;
      }
      state.locked = 'horizontal';
    }

    // Only rightward swipes trigger reply (matches native WA — a
    // leftward swipe on Android is a separate "info" gesture we don't
    // implement). Snap-back happens on touchend regardless.
    const clamped = Math.max(0, Math.min(dx, SWIPE_MAX_TRANSLATE_PX));
    state.deltaX = clamped;

    // Mutate DOM directly — bypassing React for the drag frames keeps
    // the rest of the chat list out of the render path. We restore the
    // inline styles to defaults on touchend.
    if (state.contentEl) {
      state.contentEl.style.transition = 'none';
      state.contentEl.style.transform = `translateX(${clamped}px)`;
    }
    if (state.indicatorEl) {
      // Fade in proportionally; max opacity at threshold.
      const progress = Math.min(clamped / SWIPE_THRESHOLD_PX, 1);
      state.indicatorEl.style.opacity = String(progress);
      // Slight scale-up at threshold for the "armed" feel.
      const scale = 0.6 + progress * 0.4;
      state.indicatorEl.style.transform = `translateY(-50%) scale(${scale})`;
    }
  };

  const handleBubbleTouchEnd = () => {
    const state = swipeStateRef.current;
    swipeStateRef.current = null;
    if (!state) return;
    const triggered = !state.cancelled && state.deltaX >= SWIPE_THRESHOLD_PX;
    // Snap-back animation regardless — even on a successful swipe the
    // bubble returns home, with the threaded reply UI taking the focus.
    if (state.contentEl) {
      state.contentEl.style.transition = 'transform 180ms cubic-bezier(0.2, 0.9, 0.3, 1)';
      state.contentEl.style.transform = 'translateX(0)';
    }
    if (state.indicatorEl) {
      if (triggered) {
        // Brief flash on success — more saturated, slightly larger,
        // then fade with the snap-back so it doesn't linger.
        state.indicatorEl.style.transition = 'opacity 220ms ease, transform 220ms ease';
        state.indicatorEl.style.opacity = '1';
        state.indicatorEl.style.transform = 'translateY(-50%) scale(1.15)';
        const el = state.indicatorEl;
        setTimeout(() => {
          if (!el) return;
          el.style.opacity = '0';
          el.style.transform = 'translateY(-50%) scale(0.6)';
        }, 150);
      } else {
        state.indicatorEl.style.transition = 'opacity 180ms ease, transform 180ms ease';
        state.indicatorEl.style.opacity = '0';
        state.indicatorEl.style.transform = 'translateY(-50%) scale(0.6)';
      }
    }
    if (triggered) {
      handleReplyBubble(state.msg);
    }
  };

  const handleReactionPick = (emoji) => {
    const msg = bubbleMenu?.msg;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    closeBubbleMenu();
    dispatch(reactToMessage(threadId, msg.id, emoji));
  };

  const handleReactionRemove = () => {
    const msg = bubbleMenu?.msg;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    closeBubbleMenu();
    dispatch(reactToMessage(threadId, msg.id, ''));
  };

  const handleDeleteBubble = () => {
    const msg = bubbleMenu?.msg;
    if (!msg) return;
    closeBubbleMenu();
    setRevokeTarget(msg);
  };

  const handleConfirmRevoke = () => {
    const msg = revokeTarget;
    const threadId = whatsAppChatMetaData?.thread?.id;
    setRevokeTarget(null);
    if (!msg?.id || !threadId) return;
    dispatch(revokeMessage(threadId, msg.id));
  };

  // Track D / F3 — open the ForwardPickerDialog. Two callsites:
  //   1. From the action sheet (single-message forward)
  //   2. From MultiSelectTopBar.onForward (bulk forward)
  // Snapshot the ids at open time so the dialog isn't sensitive to
  // subsequent selection changes.
  const handleForwardBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    if (msg?.id == null) {
      dispatch(updateError({ success: false, message: 'Nothing to forward' }));
      return;
    }
    setForwardPicker({ open: true, ids: [msg.id] });
  };

  const handleBulkForward = () => {
    if (!selectedIds.length) return;
    setForwardPicker({ open: true, ids: [...selectedIds] });
  };

  const closeForwardPicker = () => setForwardPicker({ open: false, ids: [] });

  const confirmForward = (targetThreadId) => {
    const { ids } = forwardPicker;
    if (!ids?.length || !targetThreadId) {
      closeForwardPicker();
      return;
    }
    dispatch(
      forwardMessages({
        messageIds: ids,
        targetThreadId,
        onSuccess: () => {
          closeForwardPicker();
          clearSelect();
        },
      })
    );
  };

  // Track D / F2 — bulk Copy. Multi-line join with blank line so the
  // pasted block keeps message-level separation. Filters out media-
  // only messages without text/caption.
  const handleBulkCopy = async () => {
    if (!selectedIds.length) return;
    const lookup = new Map((whatsappMessages ?? []).map((m) => [m.id, m]));
    const texts = selectedIds
      .map((id) => lookup.get(id))
      .filter(Boolean)
      .map((m) => plainBubbleText(m))
      .filter(Boolean);
    if (!texts.length) {
      dispatch(updateError({ success: false, message: 'Nothing to copy' }));
      return;
    }
    try {
      await navigator.clipboard.writeText(texts.join('\n\n'));
      dispatch(
        updateError({
          success: true,
          message: `Copied ${texts.length} message${texts.length === 1 ? '' : 's'}`,
        })
      );
      clearSelect();
    } catch (err) {
      dispatch(updateError({ success: false, message: 'Copy failed' }));
    }
  };

  // Track D / F2 — bulk Delete. Outbound only. Inbound messages can't
  // be revoked on Meta's side, so we surface that to the operator
  // rather than silently skipping. Now wired through Track A's
  // revokeMessage thunk (W-2) — fan out via Promise.all and toast the
  // outcome.
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    const lookup = new Map((whatsappMessages ?? []).map((m) => [m.id, m]));
    const selected = selectedIds.map((id) => lookup.get(id)).filter(Boolean);
    // Outbound = author = the connected phone number. The serializer
    // already normalises `type === 'sent'` to mean outbound for both
    // optimistic and server-confirmed messages. Skip already-revoked
    // to avoid 404s on Meta's side.
    const outbound = selected.filter((m) => m.type === 'sent' && !m.isRevoked);
    const inbound = selected.filter((m) => m.type !== 'sent');

    if (!outbound.length) {
      dispatch(
        updateError({
          success: 'warning',
          message: inbound.length ? 'Cannot delete received messages' : 'Selected messages are already deleted',
        })
      );
      return;
    }

    // Fan out the revokes; collect successes vs failures so we can
    // give the operator a single accurate toast at the end. We can't
    // know per-revoke success without re-reading state because the
    // thunk doesn't return ok/error — so we treat the dispatch chain
    // as best-effort and toast the optimistic counts. Bad responses
    // surface their own per-message error toasts via Track A's thunk.
    try {
      await Promise.all(outbound.map((m) => dispatch(revokeMessage(threadId, m.id))));
      const skipped = inbound.length;
      dispatch(
        updateError({
          success: true,
          message: skipped
            ? `Deleted ${outbound.length} message${outbound.length === 1 ? '' : 's'}, skipped ${skipped} received`
            : `Deleted ${outbound.length} message${outbound.length === 1 ? '' : 's'}`,
        })
      );
    } catch (err) {
      dispatch(
        updateError({
          success: false,
          message: 'Some messages failed to delete',
        })
      );
    } finally {
      clearSelect();
    }
  };

  // Track D / F2 + Track B — bulk Star (W-3). Fan out starMessage
  // thunks for every selected message regardless of inbound/outbound;
  // the BE accepts both per Track B's thunk semantics.
  const handleBulkStar = async () => {
    if (!selectedIds.length) return;
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!threadId) return;
    try {
      await Promise.all(selectedIds.map((id) => dispatch(starMessage(threadId, id))));
      dispatch(
        updateError({
          success: true,
          message: `Starred ${selectedIds.length} message${selectedIds.length === 1 ? '' : 's'}`,
        })
      );
    } catch (err) {
      dispatch(
        updateError({
          success: false,
          message: 'Some messages failed to star',
        })
      );
    } finally {
      clearSelect();
    }
  };

  const handleStarBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    if (msg.isStarred) {
      dispatch(unstarMessage(threadId, msg.id));
    } else {
      dispatch(starMessage(threadId, msg.id));
    }
  };

  const handlePinBubble = () => {
    const msg = bubbleMenu?.msg;
    closeBubbleMenu();
    const threadId = whatsAppChatMetaData?.thread?.id;
    if (!msg?.id || !threadId) return;
    if (msg.isRevoked) {
      dispatch(updateError({ success: false, message: 'Cannot pin a deleted message' }));
      return;
    }
    // D5 — replace existing pin only after operator confirms.
    const existingPinId = whatsAppChatMetaData?.thread?.pinned_message?.id;
    if (existingPinId && String(existingPinId) !== String(msg.id)) {
      setReplacePinTarget({ id: msg.id, threadId });
      return;
    }
    dispatch(pinMessage(threadId, msg.id));
  };

  const confirmReplacePin = () => {
    if (replacePinTarget?.id && replacePinTarget?.threadId) {
      dispatch(pinMessage(replacePinTarget.threadId, replacePinTarget.id));
    }
    setReplacePinTarget(null);
  };

  // URL → thread backup-fetch.
  //
  // The parent <Whatsapp /> component handles direct deep links
  // (`/whatsapp/thread/:threadId`) by fetching the thread off the
  // current phone profile and seeding `whatsAppChatMetaData` before
  // ChatPage tries to render. That works for the happy path, but it
  // can leave the chat panel empty when:
  //   (a) the parent's effect hasn't fired yet (slow init / mounting
  //       race),
  //   (b) the parent's fetch failed silently and metadata stayed empty,
  //       or
  //   (c) ChatPage is reached via an unexpected mount path that bypasses
  //       the parent's URL effect entirely.
  //
  // As a defensive backup, when the URL carries a `threadId` but the
  // metadata is still empty (or pointing at a different thread), kick
  // the slice's standalone direct-load path. The thunk re-checks state
  // before fetching, so it's a no-op if the parent already won the
  // race. Keyed only on `urlThreadId` + WA-init readiness so we don't
  // refire on unrelated re-renders, and on `metaThreadId` so the effect
  // settles once metadata catches up.
  const metaThreadId = whatsAppChatMetaData?.thread?.id ?? null;
  const isWhatsappInitialized = !isLoading && currentBusinessId !== -1 && currentWhatsappPhoneId !== -1;
  React.useEffect(() => {
    if (!urlThreadId) return;
    if (!isWhatsappInitialized) return;
    if (whatsappChatScreenLoading) return;
    if (String(metaThreadId) === String(urlThreadId)) return;
    // Only fire when metadata is genuinely missing for this URL —
    // either truly empty or pointing at a stale/other thread. This
    // is the safety net for case (b)/(c); case (a) is handled by
    // the parent and resolves before this branch is reached.
    const metaIsEmptyOrStale =
      Object.keys(whatsAppChatMetaData ?? {}).length === 0 ||
      (metaThreadId && String(metaThreadId) !== String(urlThreadId));
    if (!metaIsEmptyOrStale) return;
    dispatch(fetchWhatsappChatAndOtherMetaData(urlThreadId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlThreadId, isWhatsappInitialized, metaThreadId, dispatch]);

  React.useEffect(() => {
    if (
      Object.keys(whatsAppChatMetaData ?? {}).length > 0 &&
      (!whatsAppChatMetaData?.thread || !whatsAppChatMetaData?.leadDetails)
    ) {
      dispatch(fetchWhatsappChatAndOtherMetaData());
    }
    setTemplateModalOpen(false);
    setCurrentIndex(0);
    // Clear any pending attachment when switching chats
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setAttachmentCaption('');
    // Track C / F6 — drop any in-progress inline edit / open Message
    // Info drawer when the operator switches threads. Stale wamid in
    // editingBubble is harmless (no msg.wamid match → editor doesn't
    // render) but visible affordance leaks across chats are confusing.
    setEditingBubble(null);
    setMessageInfoTarget(null);
    // Auto-scroll to bottom when entering a new chat. Native WA eases
    // into the latest message when it's already close — feels alive,
    // not teleported. If the latest is far away (fresh chat open, long
    // history), an instant jump is less jarring than a long animated
    // scroll. Column-reverse container → scrollTop === 0 is the bottom,
    // so distance-from-bottom is |scrollTop|.
    setTimeout(() => {
      const el = scrollRef.current;
      if (el) {
        const distanceFromBottom = Math.abs(el.scrollTop);
        const behavior = distanceFromBottom < el.clientHeight * 1.5 ? 'smooth' : 'auto';
        el.scrollTo({ top: 0, behavior });
        setIsUserNearBottom(true);
        setUnreadWhileScrolled(0);
      }
    }, 100);
  }, [dispatch, whatsAppChatMetaData]);

  React.useEffect(() => {
    if (practiceId) dispatch(fetchAIWizardStatus(practiceId));
  }, [dispatch, practiceId]);

  React.useEffect(
    () => () => {
      // Cleanup: revoke any pending object URL on unmount
      if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
      dispatch(resetWhatsappChat());
    },
    [dispatch]
  );

  // Auto-cancel attachment when 24h window expires
  React.useEffect(() => {
    if (isWindowExpired && attachmentFile) handleCancelAttachment();
  }, [isWindowExpired]);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  }, [sendMessage]);

  // -----------------------------------------------------------
  // Tier 2 / F-20, F-21, F-22 — composer autocomplete helpers.
  //
  // The hook (composerAutocomplete) tracks which trigger ('/', ':',
  // '@') is active and the captured filter query. Each section
  // below derives its own `items` list from the current Redux /
  // emoji state, then a single `applyAutocompleteSelection` swaps
  // the in-progress token (e.g. ":smi" or "/welcome") for the
  // selected replacement (an emoji glyph, a template body, or a
  // {{merge_tag}} token).
  //
  // NB: these hooks must be declared before the early return below
  // (rules-of-hooks), even though they're only meaningful once the
  // chat metadata is loaded.
  // -----------------------------------------------------------
  const slashItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== '/') return [];
    const list = filteredTemplates || [];
    const q = (composerAutocomplete.query || '').toLowerCase();
    return list.filter((t) => !q || (t?.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query, filteredTemplates]);

  const colonItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== ':') return [];
    return searchEmojiShortcodes(composerAutocomplete.query, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query]);

  const atItems = React.useMemo(() => {
    if (composerAutocomplete.trigger !== '@') return [];
    const q = (composerAutocomplete.query || '').toLowerCase();
    return (availableMergeTags || []).filter((tag) => !q || tag.toLowerCase().includes(q)).slice(0, 8);
  }, [composerAutocomplete.trigger, composerAutocomplete.query, availableMergeTags]);

  const activeAutocompleteItems = React.useMemo(() => {
    if (composerAutocomplete.trigger === '/') return slashItems;
    if (composerAutocomplete.trigger === ':') return colonItems;
    if (composerAutocomplete.trigger === '@') return atItems;
    return [];
  }, [composerAutocomplete.trigger, slashItems, colonItems, atItems]);

  const renderAutocompleteLabel = React.useCallback(
    (item) => {
      if (composerAutocomplete.trigger === '/') return formatTemplateName(item?.name);
      if (composerAutocomplete.trigger === ':') return `${item?.emoji}  :${item?.name}:`;
      if (composerAutocomplete.trigger === '@') return `{{${item}}}`;
      return String(item);
    },
    [composerAutocomplete.trigger]
  );

  const renderAutocompleteDescription = React.useCallback(
    (item) => {
      if (composerAutocomplete.trigger === '/') return getTemplateBody(item)?.slice(0, 80) || '';
      return null;
    },
    [composerAutocomplete.trigger]
  );

  // Insert the picked replacement into the textarea, splicing out
  // the in-progress trigger token. Restores caret position to the
  // end of the inserted replacement so the operator can keep typing.
  const applyAutocompleteSelection = React.useCallback(
    (item) => {
      const textarea = inputRef.current;
      const { trigger } = composerAutocomplete;
      const { start, end } = composerAutocomplete.matchRange || {};
      if (!textarea || trigger == null) {
        composerAutocomplete.close();
        return;
      }
      let replacement = '';
      if (trigger === '/') {
        replacement = getTemplateBody(item) || '';
      } else if (trigger === ':') {
        replacement = item?.emoji || '';
      } else if (trigger === '@') {
        replacement = `{{${item}}}`;
      }

      const before = sendMessage.slice(0, start);
      const after = sendMessage.slice(end);
      const next = before + replacement + after;
      setSendMessage(next);
      persistDraft(next);
      composerAutocomplete.close();
      requestAnimationFrame(() => {
        const caretPos = (before + replacement).length;
        textarea.focus();
        try {
          textarea.selectionStart = caretPos;
          textarea.selectionEnd = caretPos;
        } catch (_) {
          /* DOM may not be ready */
        }
      });
    },
    [composerAutocomplete, sendMessage, persistDraft]
  );

  // Build the keydown handler bound to the active items list. The
  // textarea's onKeyDown calls this FIRST; if it returns true the
  // textarea's own Enter / Cmd+B / Cmd+I handlers are short-circuited.
  const handleAutocompleteKeyDown = React.useCallback(
    (evt) => {
      if (!composerAutocomplete.trigger) return false;
      const handler = composerAutocomplete.buildKeyDownHandler({
        itemCount: activeAutocompleteItems.length,
        onSelect: (idx) => {
          const item = activeAutocompleteItems[idx];
          if (item != null) applyAutocompleteSelection(item);
        },
      });
      return handler(evt);
    },
    [composerAutocomplete, activeAutocompleteItems, applyAutocompleteSelection]
  );

  // -----------------------------------------------------------
  // Tier 2 / F-16 — drag-and-drop attachment.
  //
  // Wired on the chat-panel root so the drop target is generous
  // (whole conversation area, not just the composer). Multi-file
  // drops are queued and processed sequentially through the same
  // single-file path the "+" attachment menu uses, so the operator
  // gets caption + cancel for each file in turn.
  //
  // Service-window guard: outside the window we surface a toast
  // and abort. Inside, we filter to image/video/PDF/doc MIME
  // types (anything else is silently dropped).
  // -----------------------------------------------------------
  const ALLOWED_DROP_MIME_PREFIXES = React.useMemo(() => ['image/jpeg', 'image/png', 'video/mp4', 'video/3gpp'], []);
  const ALLOWED_DROP_EXT = React.useMemo(() => ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt'], []);
  const isAttachmentEligibleFile = React.useCallback(
    (file) => {
      if (!file) return false;
      const type = (file.type || '').toLowerCase();
      if (ALLOWED_DROP_MIME_PREFIXES.some((p) => type === p || type.startsWith(p))) return true;
      const name = (file.name || '').toLowerCase();
      return ALLOWED_DROP_EXT.some((ext) => name.endsWith(ext));
    },
    [ALLOWED_DROP_MIME_PREFIXES, ALLOWED_DROP_EXT]
  );

  const beginAttachmentForFile = React.useCallback((file) => {
    if (!file) return;
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(file);
    setAttachmentCaption('');
    if (file.type?.startsWith('image/') || file.type?.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setAttachmentPreview(previewUrl);
      attachmentPreviewRef.current = previewUrl;
    } else {
      setAttachmentPreview(null);
    }
  }, []);

  const handleChatPanelDragEnter = React.useCallback((evt) => {
    const types = evt?.dataTransfer?.types;
    if (!types) return;
    const hasFiles = Array.isArray(types) ? types.includes('Files') : Array.from(types).includes('Files');
    if (!hasFiles) return;
    evt.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  }, []);
  const handleChatPanelDragOver = React.useCallback((evt) => {
    const types = evt?.dataTransfer?.types;
    if (!types) return;
    const hasFiles = Array.isArray(types) ? types.includes('Files') : Array.from(types).includes('Files');
    if (!hasFiles) return;
    evt.preventDefault();
    if (evt.dataTransfer) evt.dataTransfer.dropEffect = 'copy';
  }, []);
  const handleChatPanelDragLeave = React.useCallback((evt) => {
    if (!evt?.dataTransfer?.types) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }, []);
  const handleChatPanelDrop = React.useCallback(
    (evt) => {
      evt.preventDefault();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      const fileList = evt.dataTransfer?.files;
      if (!fileList || fileList.length === 0) return;
      if (isWindowExpired) {
        dispatch(updateError({ success: 'warning', message: '24-hour window closed — only templates can be sent.' }));
        return;
      }
      if (!isSendAllowed) {
        dispatch(updateError({ success: 'warning', message: 'Lead is not available for communication.' }));
        return;
      }
      const eligible = Array.from(fileList).filter(isAttachmentEligibleFile);
      if (eligible.length === 0) {
        dispatch(updateError({ success: 'warning', message: 'Unsupported file type.' }));
        return;
      }
      const [first, ...rest] = eligible;
      beginAttachmentForFile(first);
      if (rest.length > 0) setPendingDropQueue((q) => [...q, ...rest]);
    },
    [isWindowExpired, isSendAllowed, isAttachmentEligibleFile, beginAttachmentForFile, dispatch]
  );

  // When the current attachment clears (sent or cancelled) and we
  // still have queued files, advance to the next one.
  React.useEffect(() => {
    if (attachmentFile) return;
    if (pendingDropQueue.length === 0) return;
    const [next, ...rest] = pendingDropQueue;
    setPendingDropQueue(rest);
    beginAttachmentForFile(next);
  }, [attachmentFile, pendingDropQueue, beginAttachmentForFile]);

  // -----------------------------------------------------------
  // Tier 2 / F-17 — paste-from-clipboard handler.
  // Returns true when the paste was consumed (an image attached);
  // false to let the existing text-paste path run.
  // -----------------------------------------------------------
  const handleComposerPaste = React.useCallback(
    (evt) => {
      const items = evt?.clipboardData?.items;
      if (!items || items.length === 0) return false;
      let imageFile = null;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item?.kind === 'file' && (item.type || '').startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            imageFile = file;
            break;
          }
        }
      }
      if (!imageFile) return false;
      evt.preventDefault();
      if (isWindowExpired) {
        dispatch(updateError({ success: 'warning', message: '24-hour window closed — only templates can be sent.' }));
        return true;
      }
      if (!isSendAllowed) {
        dispatch(updateError({ success: 'warning', message: 'Lead is not available for communication.' }));
        return true;
      }
      beginAttachmentForFile(imageFile);
      return true;
    },
    [isWindowExpired, isSendAllowed, beginAttachmentForFile, dispatch]
  );

  // Tier 3 / F-33 — these hooks must be declared before the early
  // return below (rules-of-hooks). Both are pure derivations off
  // already-declared state, so the early-return path simply skips
  // computing the divider; nothing in the placeholder pane references
  // them.
  //
  // Index of the OLDEST unread inbound message in `whatsappMessages`.
  // The list is column-reverse (newest first), so counting
  // `unreadDividerCount` inbound bubbles from index 0 lands on the
  // boundary message — render the divider after that index in JSX so
  // it sits visually ABOVE it. We only count `received` bubbles
  // (operator-side: from the patient). Returns -1 when the divider
  // should not render (count <=0, no messages, or fewer inbound
  // messages than the snapshot count — e.g. older history not yet
  // loaded).
  const unreadDividerIndex = React.useMemo(() => {
    if (!unreadDividerCount || unreadDividerCount <= 0) return -1;
    if (!whatsappMessages?.length) return -1;
    let inboundSeen = 0;
    for (let i = 0; i < whatsappMessages.length; i += 1) {
      const m = whatsappMessages[i];
      if (m?.type === 'received') {
        inboundSeen += 1;
        if (inboundSeen === unreadDividerCount) return i;
      }
    }
    return -1;
  }, [whatsappMessages, unreadDividerCount]);

  // Jump-button handler — smooth-scrolls the divider into view.
  const scrollDividerIntoView = React.useCallback(() => {
    const node = unreadDividerRef.current;
    if (!node) return;
    try {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      // Older browsers may throw on the options object — fall back
      // to the no-arg form so the click still scrolls.
      node.scrollIntoView();
    }
  }, []);

  if (Object.keys(whatsAppChatMetaData ?? {}).length === 0 || isLoading) {
    return <SearchLeadForWhatsapp />;
  }

  const handleSendMessageChange = (e) => {
    const next = e?.target?.value ?? '';
    setSendMessage(next);
    // F-18 — push every change into the draft hook (it debounces
    // its own localStorage writes).
    persistDraft(next);
    // F-20 / F-21 / F-22 — re-evaluate autocomplete trigger off
    // the new value + the textarea's caret. We read selectionStart
    // synchronously off the event target so the regex sees the
    // pre-React state.
    const caret = e?.target?.selectionStart ?? next.length;
    composerAutocomplete.evaluate(next, caret);
  };

  const handleEmojiSelect = (emoji) => {
    const textarea = inputRef.current;
    if (!textarea) {
      setSendMessage((prev) => prev + emoji);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = sendMessage.substring(0, start) + emoji + sendMessage.substring(end);
    setSendMessage(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + emoji.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
    });
  };

  const applyFormatting = (marker) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = sendMessage.substring(start, end);

    let newValue;
    let cursorPos;

    if (selectedText) {
      newValue = sendMessage.substring(0, start) + marker + selectedText + marker + sendMessage.substring(end);
      cursorPos = end + marker.length * 2;
    } else {
      newValue = sendMessage.substring(0, start) + marker + marker + sendMessage.substring(end);
      cursorPos = start + marker.length;
    }

    setSendMessage(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      if (selectedText) {
        textarea.selectionStart = start;
        textarea.selectionEnd = cursorPos;
      } else {
        textarea.selectionStart = cursorPos;
        textarea.selectionEnd = cursorPos;
      }
    });
  };

  const sendWhatsappMessageHandler = (message) => {
    if (!(message?.trim()?.length > 0)) return;
    // Show optimistic "sending" bubble but keep the message in the input
    // until we know the send succeeded. On failure, restore the input.
    const savedMessage = message;
    // Snapshot the active reply BEFORE we clear it. If the send fails we
    // restore both the text and the reply so the operator doesn't lose
    // the threading intent.
    const savedReply = replyingTo;
    const repliedToWamid = replyingTo?.wamid || null;
    setSendMessage('');
    if (inputRef.current) inputRef.current.value = '';
    setReplyingTo(null);
    // F-18 — drop the saved draft once we've optimistically taken
    // the message off the screen. If the send fails the onFailure
    // handler restores both the input and the draft.
    clearPersistedDraft();
    dispatch(sendWhatsappMessage(message, 'text'));
    if (onMessageSent) onMessageSent();
    setWhatsappMessages((prv) => [
      {
        id: Date.now(),
        timestamp: 'Now',
        isMessageInteractive: false,
        content: message,
        type: 'sent',
        status: 'sending',
        createdAt: new Date().toISOString(),
        // Mirror the threaded structure on the optimistic bubble so the
        // operator sees the quote-card immediately, before the server
        // round-trip resolves and re-fetches the canonical message list.
        repliedTo: savedReply
          ? {
              id: null,
              wamid: savedReply.wamid,
              body: savedReply.body,
              messageType: 'text',
              fromWaid: savedReply.fromWaid,
              isFromMe: savedReply.isFromMe,
            }
          : null,
      },
      ...(prv ?? []),
    ]);
    dispatch(
      sendWhatsappMessage(message, 'text', null, {
        repliedToWamid,
        onFailure: () => {
          // Restore the message and the reply context so the user
          // doesn't lose their work.
          setSendMessage(savedMessage);
          if (inputRef.current) inputRef.current.value = savedMessage;
          setReplyingTo(savedReply);
          // F-18 — restore the persisted draft too so a refresh
          // won't lose the failed send.
          persistDraft(savedMessage);
          // Remove the optimistic "sending" bubble
          setWhatsappMessages((prv) => (prv ?? []).filter((m) => m.status !== 'sending'));
        },
      })
    );
  };

  const sendWhatsappMessageTemplate = (message) => {
    if (!(message?.name?.trim()?.length > 0)) return;
    setTemplateModalOpen(false);
    setCurrentIndex(0);
    setSendMessage('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    dispatch(sendWhatsappMessage(message?.name ?? '', 'template', handleCloseChat));
    // Remove banner messages (windowAlert and templateRequired) and add the sent template + info message
    setWhatsappMessages((prv) => {
      // Filter out the banner messages
      const filteredMessages = (prv ?? []).filter(
        (msg) => msg.type !== 'windowAlert' && msg.type !== 'templateRequired'
      );
      // Add the new template message and an info message
      return [
        {
          id: `System${Date.now()}templateSentInfo`,
          type: 'system',
          content: 'Template sent! Free messaging will be available once the recipient responds to your message.',
          freeze: true,
        },
        {
          id: Date.now(),
          timestamp: 'Now',
          isMessageInteractive: false,
          content: message?.components?.map((component) => component?.text)?.join('\n'),
          type: 'sent',
          status: 'sending',
          createdAt: new Date().toISOString(),
        },
        ...filteredMessages,
      ];
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentFile(file);
    setAttachmentCaption('');
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      const previewUrl = URL.createObjectURL(file);
      setAttachmentPreview(previewUrl);
      attachmentPreviewRef.current = previewUrl;
    } else {
      setAttachmentPreview(null);
      attachmentPreviewRef.current = null;
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  // Attachment "+" popover — opens a native-WA-style grid of coloured
  // shortcuts. Each shortcut retargets the same hidden <input type="file">
  // with a narrower `accept` attribute so the OS picker scopes to the right
  // file family before handing back to handleFileSelect.
  const openAttachmentMenu = (e) => {
    setAttachmentMenuAnchor(e.currentTarget);
  };
  const closeAttachmentMenu = () => setAttachmentMenuAnchor(null);
  const triggerFilePicker = (acceptOverride) => {
    const input = fileInputRef.current;
    if (!input) return;
    // Default = original accept list (kept in sync with the <input> below).
    const defaultAccept = 'image/jpeg,image/png,video/mp4,video/3gpp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
    input.setAttribute('accept', acceptOverride || defaultAccept);
    closeAttachmentMenu();
    // Defer click to next tick so the popover close transition doesn't
    // race the file picker opening.
    requestAnimationFrame(() => input.click());
  };

  const handleCancelAttachment = () => {
    if (attachmentPreviewRef.current) URL.revokeObjectURL(attachmentPreviewRef.current);
    attachmentPreviewRef.current = null;
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setAttachmentCaption('');
  };

  const handleSendAttachment = () => {
    if (!attachmentFile || !isSendAllowed || isWindowExpired) return;
    dispatch(sendWhatsappMediaMessage(attachmentFile, attachmentCaption.trim() || null));
    // Add optimistic message
    const isImage = attachmentFile.type?.startsWith('image/');
    setWhatsappMessages((prv) => [
      {
        id: Date.now(),
        timestamp: 'Now',
        isMessageInteractive: false,
        content: attachmentCaption.trim() || (isImage ? '[Image]' : `[${attachmentFile.name}]`),
        type: 'sent',
        status: 'sending',
        createdAt: new Date().toISOString(),
      },
      ...(prv ?? []),
    ]);
    handleCancelAttachment();
  };

  const avatarColor = getAvatarColor(whatsAppChatMetaData?.name);

  // Group messages by date for date dividers
  // Returns the date to show ABOVE this message's group (rendered after message due to column-reverse)
  const getDateDividerKey = (index) => {
    const currentMsg = whatsappMessages[index];
    if (!currentMsg?.createdAt) return null;
    if (currentMsg.type === 'system' || currentMsg.type === 'windowAlert' || currentMsg.type === 'templateRequired')
      return null;

    const currentDate = getDateKey(currentMsg.createdAt);

    // For the oldest message in the list, always show its date
    if (index >= whatsappMessages.length - 1) {
      return formatDateForDivider(currentMsg.createdAt);
    }

    // Check the next message (which is older in time due to array order)
    const nextMsg = whatsappMessages[index + 1];
    // Skip system messages when checking
    if (nextMsg?.type === 'system' || nextMsg?.type === 'windowAlert' || nextMsg?.type === 'templateRequired') {
      // Find the next actual message
      let nextActualIndex = index + 2;
      while (nextActualIndex < whatsappMessages.length) {
        const msg = whatsappMessages[nextActualIndex];
        if (msg?.type !== 'system' && msg?.type !== 'windowAlert' && msg?.type !== 'templateRequired') {
          const nextDate = getDateKey(msg.createdAt);
          if (currentDate !== nextDate) {
            return formatDateForDivider(currentMsg.createdAt);
          }
          return null;
        }
        nextActualIndex += 1;
      }
      // No more actual messages, show this date
      return formatDateForDivider(currentMsg.createdAt);
    }

    if (!nextMsg?.createdAt) return formatDateForDivider(currentMsg.createdAt);

    const nextDate = getDateKey(nextMsg.createdAt);
    if (currentDate !== nextDate) {
      return formatDateForDivider(currentMsg.createdAt);
    }
    return null;
  };

  // AI Assistant Handlers
  const handleAiButtonClick = (event) => {
    setAiMenuAnchor(event.currentTarget);
    setAiMenuOpen(true);
  };

  const handleAiMenuClose = () => {
    setAiMenuOpen(false);
    setAiMenuAnchor(null);
  };

  const buildAiContext = () => {
    const phoneNumber = whatsAppChatMetaData?.phoneNumber;
    // API returns newest first (index 0 = latest message)
    // slice(0, 15) gets the 15 most recent messages
    // reverse() converts to chronological order (oldest first, newest last) for AI readability
    const recentMessages =
      whatsappChats
        ?.slice(0, 15)
        ?.map((msg) => ({
          role: arePhoneNumsEqual(msg.from_waid, phoneNumber) ? 'patient' : 'staff',
          content: msg.body || msg.content || '',
          timestamp: msg.created_at,
        }))
        .reverse() || [];

    return {
      leadName: whatsAppChatMetaData?.name,
      leadPhone: phoneNumber,
      treatment: whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment?.name || '',
      treatmentStatus: whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.status || '',
      recentMessages,
      draftMessage: sendMessage,
    };
  };

  const handleAiSelectAction = (actionType, extraContext = {}) => {
    const context = { ...buildAiContext(), ...extraContext };
    setLastAiAction(actionType);
    setLastAiContext(context);
    dispatch(generateAiResponse(actionType, context));
  };

  const handleAiResultClose = () => {
    dispatch(clearAiAssistant());
    setLastAiAction(null);
    setLastAiContext(null);
  };

  const handleAiResultSend = (content) => {
    sendWhatsappMessageHandler(content);
    handleAiResultClose();
  };

  const handleAiRegenerate = () => {
    if (lastAiAction && lastAiContext) {
      dispatch(generateAiResponse(lastAiAction, lastAiContext));
    }
  };

  return (
    <Box
      sx={styles.containerStyle}
      onDragEnter={handleChatPanelDragEnter}
      onDragOver={handleChatPanelDragOver}
      onDragLeave={handleChatPanelDragLeave}
      onDrop={handleChatPanelDrop}
    >
      <GlobalStyles
        styles={{
          '.wa-highlight': {
            backgroundColor: '#FFFDE7',
            transition: 'background-color 0.6s ease-out',
          },
        }}
      />
      {/* Tier 2 / F-16 — drop overlay. Pointer-events:none so the
                overlay never intercepts the drop event itself; the root
                Box handler still receives it. */}
      {isDragOver && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 12,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 171, 85, 0.08)',
            border: `2px dashed ${COLORS.primary}`,
            borderRadius: '12px',
            margin: '8px',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.92)',
              padding: '12px 18px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
            }}
          >
            <Icon icon="mdi:tray-arrow-down" width={22} height={22} style={{ color: COLORS.primary }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>Drop to send</Typography>
          </Box>
        </Box>
      )}
      {/* Header */}
      <Box sx={styles.header}>
        {/* Contact Row */}
        <Box sx={styles.chatContactRow}>
          <Box sx={styles.avatarBlock}>
            {isMobile && !embedded && onBack && (
              <IconButton onClick={onBack} aria-label="Back to conversations" sx={{ minWidth: 44, minHeight: 44 }}>
                <Icon icon="mdi:arrow-left" width={20} />
              </IconButton>
            )}
            <Avatar
              {...stringAvatar(whatsAppChatMetaData?.avatar, whatsAppChatMetaData?.phoneNumber, {
                width: '36px',
                height: '36px',
                fontSize: '14px',
                backgroundColor: avatarColor,
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
              })}
              onClick={onToggleDetails}
            />
            <Box sx={styles.userInfo}>
              <Box sx={styles.chatName}>
                <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600, color: COLORS.textPrimary }}>
                  {whatsAppChatMetaData?.name}
                </Typography>
                {whatsAppChatMetaData?.isCloseObject?.isClosed && (
                  <Tooltip title="Click to reopen" arrow>
                    <Box
                      component="span"
                      onClick={() =>
                        dispatch(
                          reOpenWhatsappConversationWithReason(
                            whatsAppChatMetaData?.thread?.id,
                            whatsAppChatMetaData?.leadDetails?.id
                          )
                        )
                      }
                      sx={{
                        ...styles.statusBadge,
                        backgroundColor: COLORS.errorBg,
                        color: COLORS.errorDark,
                        cursor: 'pointer',
                      }}
                    >
                      {whatsAppChatMetaData?.isCloseObject?.closedReason || 'Closed'}
                    </Box>
                  </Tooltip>
                )}
                {!whatsAppChatMetaData?.leadDetails?.id && (
                  <Box
                    component="span"
                    sx={{ ...styles.statusBadge, backgroundColor: COLORS.errorBg, color: COLORS.errorDark }}
                  >
                    Not Registered
                  </Box>
                )}
                {whatsAppChatMetaData?.leadDetails?.archived_at && (
                  <Box
                    component="span"
                    sx={{ ...styles.statusBadge, backgroundColor: COLORS.errorBg, color: COLORS.errorDark }}
                  >
                    Archived
                  </Box>
                )}
                {/* 24-hour Meta service-window pill. Inside the
                                    window, free-form text + utility templates
                                    are billed at zero. Outside, only templates
                                    deliver (paid). Hidden until we actually
                                    have thread state — avoids a flash on
                                    first load of an empty thread. */}
                {whatsAppChatMetaData?.thread?.id &&
                  (serviceWindow.open ? (
                    <Tooltip
                      title={
                        serviceWindow.closesAt
                          ? `Free-form & utility messages are free until ${serviceWindow.closesAt.toLocaleString()}`
                          : 'Free-form messages allowed inside the 24h window'
                      }
                      arrow
                    >
                      <Box
                        component="span"
                        sx={{
                          ...styles.statusBadge,
                          backgroundColor: COLORS.primaryLight,
                          color: COLORS.primaryDark,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: COLORS.primary,
                            display: 'inline-block',
                          }}
                        />
                        {serviceWindow.msRemaining != null
                          ? `Window ${formatWindowRemaining(serviceWindow.msRemaining) || 'open'}`
                          : 'Window open'}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Outside the 24h Meta service window — only approved templates can be sent." arrow>
                      <Box
                        component="span"
                        sx={{
                          ...styles.statusBadge,
                          backgroundColor: COLORS.warningBg,
                          color: COLORS.warningDark,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: COLORS.warning,
                            display: 'inline-block',
                          }}
                        />
                        Window closed
                      </Box>
                    </Tooltip>
                  ))}
              </Box>
              <Typography component="div" sx={styles.chatSubtitle}>
                {whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment && (
                  <>{whatsAppChatMetaData?.leadDetails?.lead_treatments?.[0]?.treatment}</>
                )}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0, minHeight: 32 }}>
            {whatsAppChatMetaData?.thread?.unread_count > 0 && (
              <Box component="span" sx={styles.statusBadge}>
                {whatsAppChatMetaData.thread.unread_count} unread
              </Box>
            )}
            {/* Track B — Starred messages drawer trigger.
                            Shown whenever the thread is loaded; clicking
                            opens the right-side drawer that lists starred
                            messages with a "This chat" / "All chats"
                            toggle. */}
            {whatsAppChatMetaData?.thread?.id && (
              <Tooltip title="Starred messages" arrow>
                <IconButton
                  onClick={() => setStarredPanelOpen(true)}
                  sx={styles.createLeadBtn}
                  aria-label="Open starred messages"
                >
                  <Icon icon="mdi:star-outline" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
            {!embedded && !whatsAppChatMetaData?.leadDetails?.id && (
              <Tooltip title="Mark as unread" arrow>
                <IconButton
                  onClick={() => dispatch(markAsUnRead(whatsAppChatMetaData?.thread?.id))}
                  disabled={whatsppSendLoading}
                  sx={styles.createLeadBtn}
                >
                  <Icon icon="solar:chat-unread-outline" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
            {!embedded && !whatsAppChatMetaData?.leadDetails?.id && (
              <Tooltip title="Create Lead" arrow>
                <IconButton
                  onClick={() => quickActionsRef.current?.createLead?.()}
                  disabled={whatsppSendLoading}
                  sx={styles.createLeadBtn}
                >
                  <Icon icon="bi:person-add" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
            {(embedded || whatsAppChatMetaData?.leadDetails?.id) && (
              <Tooltip title="Actions" arrow>
                <IconButton
                  onClick={(e) => quickActionsRef.current?.openMenu?.(e)}
                  disabled={whatsppSendLoading || !whatsAppChatMetaData?.leadDetails?.id}
                  sx={styles.createLeadBtn}
                >
                  <Icon icon="mdi:dots-vertical" width={20} />
                </IconButton>
              </Tooltip>
            )}
            {/* Focus mode (#24) — collapses CRM nav rail to its mini variant */}
            {!embedded && !isMobile && onToggleFocusMode && (
              <Tooltip title={isFocusMode ? 'Exit focus mode' : 'Focus mode'} arrow>
                <IconButton
                  onClick={onToggleFocusMode}
                  sx={styles.createLeadBtn}
                  aria-label={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
                >
                  <Icon icon={isFocusMode ? 'mdi:arrow-collapse-all' : 'mdi:arrow-expand-all'} width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
            {/* Fullscreen toggle (#25) — complements the one on Contacts */}
            {!embedded && !isMobile && onToggleFullscreen && (
              <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} arrow>
                <IconButton
                  onClick={onToggleFullscreen}
                  sx={styles.createLeadBtn}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  <Icon icon={isFullscreen ? 'mdi:fullscreen-exit' : 'mdi:fullscreen'} width={20} height={20} />
                </IconButton>
              </Tooltip>
            )}
            {!hideResolve && !whatsAppChatMetaData?.isCloseObject?.isClosed && (
              <Tooltip title="Resolve" arrow>
                <IconButton
                  onClick={() => (onOpenResolve ? onOpenResolve() : setOpenCloseConversation(true))}
                  disabled={whatsppSendLoading}
                  sx={styles.resolveBtn}
                  aria-label="Resolve conversation"
                >
                  <Icon icon="mdi:check-circle-outline" width={20} />
                </IconButton>
              </Tooltip>
            )}
            {!embedded && (
              <Tooltip title="Close">
                <IconButton onClick={handleCloseChat} sx={styles.closeBtn} aria-label="Close chat">
                  <Icon icon="mdi:close" width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Quick Close Popover */}
        <Popover
          open={Boolean(quickCloseAnchor)}
          anchorEl={quickCloseAnchor}
          onClose={handleQuickCloseClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5 } } }}
        >
          <Box sx={{ py: 0.5 }}>
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Close Reason
            </Typography>
            {practiceChoices?.map((choice) => (
              <MenuItem
                key={choice}
                onClick={() => handleQuickClose(choice)}
                sx={{ fontSize: '13px', py: 0.75, px: 2 }}
              >
                {choice}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => {
                handleQuickCloseClose();
                setOpenCloseConversation(true);
              }}
              sx={{ fontSize: '13px', py: 0.75, px: 2, gap: 1, color: COLORS.textSecondary }}
            >
              <Icon icon="mdi:information-outline" width={16} height={16} />
              View lead details first...
            </MenuItem>
          </Box>
        </Popover>

        {/* Tag Info */}
        {whatsAppChatMetaData?.tags?.[0]?.name && !whatsappChatScreenLoading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: 0.5 }}>
            <Tooltip title="Click to remove tag" placement="top" arrow>
              <Box
                component="span"
                onClick={() => dispatch(removeWhatsappTag(whatsAppChatMetaData?.thread?.id))}
                sx={{
                  ...styles.tagStyles,
                  background: whatsAppChatMetaData?.tags?.[0]?.colour ?? COLORS.warning,
                  color: COLORS.paper,
                  cursor: 'pointer',
                }}
              >
                {whatsAppChatMetaData?.tags?.[0]?.name}
              </Box>
            </Tooltip>
          </Box>
        )}

        {/* Quick Actions - Inside Header */}
        <QuickActions
          onToggleDetails={onToggleDetails}
          detailsOpen={detailsOpen}
          embedded={embedded}
          whatsppSendLoading={whatsppSendLoading}
          isSendAllowed={isSendAllowed}
          actionsRef={quickActionsRef}
        />
      </Box>

      {/* Track B — Pinned message banner. Sits between the header
                and the messages stream. Renders nothing when no pin is
                set; the banner is sticky-by-position because it's fixed
                under the chat header in the document order. */}
      {whatsAppChatMetaData?.thread?.pinned_message?.id && (
        <PinnedMessageBanner
          pinnedMessage={whatsAppChatMetaData.thread.pinned_message}
          threadId={whatsAppChatMetaData.thread.id}
        />
      )}

      {/* Track D / F2 — Multi-select top bar. Sticks below the
                header when any bubble is selected. */}
      <MultiSelectTopBar
        open={isSelecting}
        count={selectedIds.length}
        onCancel={clearSelect}
        onForward={handleBulkForward}
        onCopy={handleBulkCopy}
        onStar={handleBulkStar}
        onDelete={handleBulkDelete}
      />

      {/* Tier 3 / F-33 — floating "N unread ↓" jump button.
                Visible when:
                  - the thread had >0 unread when opened (snapshot), AND
                  - the divider is currently OFF-screen.
                Hides on viewport entry (IntersectionObserver) or count
                reset to 0. Click smooth-scrolls the divider into view —
                does not duplicate `mark_as_read` because the existing
                fetchWhatsappChatAndOtherMetaData side-effect already
                fired when the operator opened the thread. */}
      {unreadDividerCount > 0 && unreadDividerIndex >= 0 && !isUnreadDividerVisible && (
        <Box
          component="button"
          type="button"
          onClick={scrollDividerIntoView}
          sx={styles.unreadJumpBtn}
          aria-label={`${unreadDividerCount} unread message${unreadDividerCount === 1 ? '' : 's'}, jump to first`}
        >
          {`${unreadDividerCount} unread`}
          <Icon icon="mdi:arrow-down" width={14} height={14} />
        </Box>
      )}

      {/* Messages + Footer */}
      {
        <>
          {whatsappChatScreenLoading ? (
            <Box sx={styles.messagePane}>
              <WhatsappSkeleton />
            </Box>
          ) : (
            <Box
              id="chat-scrollable"
              ref={scrollRef}
              onClick={(e) => {
                // Track D / F2 — tap empty space exits multi-select.
                // Bubble clicks stop here only when the target is the
                // scrollable container itself (or its direct empty
                // children), not a bubble.
                if (!isSelecting) return;
                const { target } = e;
                if (!(target instanceof Element)) return;
                if (target.closest('[data-message-id]')) return;
                if (target.closest('[role="toolbar"]')) return;
                clearSelect();
              }}
              sx={{
                ...styles.messagePane,
                display: 'flex',
                flexDirection: 'column-reverse',
                position: 'relative',
              }}
            >
              <InfiniteScroll
                dataLength={whatsappMessages?.length ?? 0}
                next={fetchMoreMessages}
                hasMore={whatsAppChatNextAPI?.length > 0}
                inverse
                scrollableTarget="chat-scrollable"
                loader={
                  whatsappChatNextAPILoading ? (
                    <Box sx={{ textAlign: 'center', padding: '16px' }}>
                      <Typography variant="caption" sx={{ color: COLORS.textDisabled }}>
                        Loading...
                      </Typography>
                    </Box>
                  ) : null
                }
                endMessage={
                  <Box sx={styles.messagesStart}>
                    <Typography variant="caption">You have reached the beginning of the chat</Typography>
                  </Box>
                }
                style={{ display: 'flex', flexDirection: 'column-reverse', overflowX: 'hidden' }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column-reverse', overflowX: 'hidden' }}>
                  {whatsappMessages?.map((msg, index) => {
                    // Window Alert — removed from chat flow (shown only in footer action bar)
                    if (msg.type === 'windowAlert') {
                      return null;
                    }

                    // Template Required Notice — removed (footer already shows this info)
                    if (msg.type === 'templateRequired') {
                      return null;
                    }

                    // Compute same-sender run boundaries. The list is rendered
                    // column-reverse, so index+1 is the visually ABOVE neighbor
                    // (older) and index-1 is visually BELOW (newer).
                    //   - isFirstInGroup (tail-bearing) = topmost in a run
                    //     → neighbor ABOVE is a different type.
                    //   - isLastInGroup = bottommost in a run
                    //     → neighbor BELOW is a different type.
                    const isBubbleMsg = msg.type === 'sent' || msg.type === 'received';
                    let isFirstInGroup = false;
                    let isLastInGroup = false;
                    if (isBubbleMsg) {
                      const prevAbove = whatsappMessages[index + 1];
                      const prevBelow = whatsappMessages[index - 1];
                      isFirstInGroup = !prevAbove || prevAbove.type !== msg.type;
                      isLastInGroup = !prevBelow || prevBelow.type !== msg.type;
                    }

                    // System Messages
                    if (msg.type === 'system') {
                      // addMediaButtons: render as inline button (no system pill wrapper)
                      if (msg?.specialType === 'addMediaButtons' && whatsAppChatMetaData?.leadDetails?.id) {
                        return (
                          <Box key={msg.id} sx={{ margin: '10px 0', textAlign: 'left', pl: '12px' }}>
                            <AddMediaButtons thread={msg} leadDetails={whatsAppChatMetaData?.leadDetails} />
                          </Box>
                        );
                      }
                      return (
                        <Box key={msg.id} sx={styles.system}>
                          <Typography sx={{ fontSize: '13px' }}>{msg.content}</Typography>
                        </Box>
                      );
                    }

                    // Date Divider (show after this message if date changes)
                    const dateDivider = getDateDividerKey(index);

                    // Determine bubble style — template messages get lavender, regular sent get green
                    let bubbleTypeStyle = styles.messageBubbleReceived;
                    if (msg.type === 'sent') {
                      bubbleTypeStyle = msg.isSpecialTemplateMessage
                        ? styles.messageBubbleTemplate
                        : styles.messageBubbleSent;
                    }
                    // Only the first bubble in a same-sender run gets the CSS tail.
                    let bubbleTailStyle = null;
                    if (isFirstInGroup) {
                      bubbleTailStyle =
                        msg.type === 'sent' ? styles.messageBubbleFirstSent : styles.messageBubbleFirstReceived;
                    }

                    // Arrival animation — only for newly-arrived RECEIVED messages
                    // (not during initial thread load, not for the user's own sends
                    // which already appear instantly via the optimistic bubble).
                    const isNewArrival =
                      msg.type === 'received' &&
                      msg.id != null &&
                      !seenMessageIdsRef.current.has(msg.id) &&
                      seededForThreadRef.current != null;

                    // Collapsed template message — compact pill, expandable on click
                    const isTemplateExpanded = expandedTemplates.has(msg.id);
                    if (msg.isSpecialTemplateMessage && !isTemplateExpanded) {
                      return (
                        <React.Fragment key={msg.id}>
                          <Box
                            sx={{
                              ...styles.messageWrapper,
                              ...styles.messageWrapperSent,
                              ...(isLastInGroup ? styles.messageWrapperGroupEnd : {}),
                            }}
                          >
                            <Box sx={styles.messageContent}>
                              <Box
                                onClick={() => setExpandedTemplates((prev) => new Set([...prev, msg.id]))}
                                sx={styles.templateCollapsed}
                              >
                                <Tooltip
                                  title="Automated template sent to patient by the system — click to expand"
                                  arrow
                                  placement="left"
                                >
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
                                  >
                                    <Icon
                                      icon="mdi:robot-outline"
                                      width={14}
                                      height={14}
                                      style={{ color: COLORS.primary }}
                                    />
                                  </Box>
                                </Tooltip>
                                <Typography component="span" sx={{ fontSize: '11px', color: COLORS.textDisabled }}>
                                  {msg.timestamp}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          {/* Tier 3 / F-33 — first-unread divider (template path).
                                                Rendered between the message and the date divider in
                                                JSX; column-reverse places it visually above the
                                                boundary message and below the date label. */}
                          {index === unreadDividerIndex && unreadDividerCount > 0 && (
                            <UnreadDivider ref={unreadDividerRef} count={unreadDividerCount} />
                          )}
                          {dateDivider && (
                            <Box sx={styles.dateDivider}>
                              <Typography sx={styles.dateDividerText}>{dateDivider}</Typography>
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    }

                    // Mobile-only swipe-to-reply: only attach touch handlers
                    // when on mobile AND the bubble is a reply-eligible target
                    // (mirrors the right-click menu's eligibility rules).
                    const swipeEnabled = isMobile && isBubbleReplyEligible(msg);

                    return (
                      <React.Fragment key={msg.id}>
                        {/* Message */}
                        <Box
                          id={msg.id != null ? `wa-msg-${msg.id}` : undefined}
                          data-message-id={msg.id != null ? msg.id : undefined}
                          sx={{
                            ...styles.messageWrapper,
                            ...(msg.type === 'sent' ? styles.messageWrapperSent : styles.messageWrapperReceived),
                            ...(isLastInGroup ? styles.messageWrapperGroupEnd : {}),
                            ...(isNewArrival ? styles.messageArriveReceived : {}),
                            // Anchor the swipe-reply indicator absolutely against
                            // the wrapper so it stays put while the bubble slides.
                            ...(swipeEnabled ? { position: 'relative', overflow: 'visible' } : {}),
                          }}
                        >
                          {/* Swipe-to-reply indicator — fades in as the bubble is
                                                dragged right, full opacity at threshold. Mobile only.
                                                Sits on the LEFT of the wrapper regardless of bubble
                                                alignment, matching native WA. */}
                          {swipeEnabled && (
                            <Box
                              data-swipe-indicator="1"
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%) scale(0.6)',
                                opacity: 0,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: COLORS.primaryLighter,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                zIndex: 0,
                                willChange: 'opacity, transform',
                              }}
                            >
                              <Icon icon="mdi:reply" width={16} height={16} style={{ color: COLORS.primary }} />
                            </Box>
                          )}
                          {/* Avatar removed — native WhatsApp doesn't show avatars in 1:1 chats */}
                          <Box
                            sx={{
                              ...styles.messageContent,
                              ...(swipeEnabled ? { willChange: 'transform', position: 'relative', zIndex: 1 } : {}),
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {/* Track D / F2 — selection checkbox. Renders only when in
                                                    multi-select mode. The actual toggle happens via the bubble
                                                    onClick (see lp.onClick override above) — we don't bind a
                                                    click handler here to avoid double-toggle. */}
                            {isSelecting && isBubbleActionEligible(msg) && msg.id != null && (
                              <Box
                                aria-label={selectedIds.includes(msg.id) ? 'Deselect message' : 'Select message'}
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  border: `2px solid ${selectedIds.includes(msg.id) ? COLORS.primary : COLORS.divider}`,
                                  backgroundColor: selectedIds.includes(msg.id) ? COLORS.primary : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'all 120ms ease',
                                }}
                              >
                                {selectedIds.includes(msg.id) ? (
                                  <Icon icon="mdi:check" width={14} height={14} style={{ color: '#FFFFFF' }} />
                                ) : null}
                              </Box>
                            )}
                            <Box
                              sx={{
                                ...styles.messageBubble,
                                ...bubbleTypeStyle,
                                ...(bubbleTailStyle || {}),
                                ...(msg.isSpecialTemplateMessage && isTemplateExpanded ? { cursor: 'pointer' } : {}),
                                ...(selectedIds.includes(msg.id)
                                  ? {
                                      outline: `2px solid ${COLORS.primary}`,
                                      outlineOffset: '2px',
                                    }
                                  : {}),
                              }}
                              {...(() => {
                                const lp = longPress.bind(msg);
                                const handlers = { onContextMenu: lp.onContextMenu };
                                if (swipeEnabled) {
                                  handlers.onTouchStart = (e) => {
                                    lp.onTouchStart(e);
                                    handleBubbleTouchStart(e, msg);
                                  };
                                  handlers.onTouchMove = (e) => {
                                    lp.onTouchMove(e);
                                    handleBubbleTouchMove(e);
                                    // Once swipe direction lock resolves to horizontal,
                                    // cancel the long-press timer to prevent the action
                                    // sheet from opening mid-swipe.
                                    if (swipeStateRef.current?.locked === 'horizontal') {
                                      longPress.cancelTimer();
                                    }
                                  };
                                  handlers.onTouchEnd = (e) => {
                                    lp.onTouchEnd(e);
                                    handleBubbleTouchEnd(e);
                                  };
                                  handlers.onTouchCancel = (e) => {
                                    lp.onTouchCancel(e);
                                    handleBubbleTouchEnd(e);
                                  };
                                } else {
                                  handlers.onTouchStart = lp.onTouchStart;
                                  handlers.onTouchMove = lp.onTouchMove;
                                  handlers.onTouchEnd = lp.onTouchEnd;
                                  handlers.onTouchCancel = lp.onTouchCancel;
                                }
                                // Track D / F2 — when in multi-select mode, every tap on
                                // a bubble toggles its membership. We still let lp.onClick
                                // run first so shift+click on desktop keeps working.
                                if (isSelecting) {
                                  handlers.onClick = (e) => {
                                    lp.onClick(e);
                                    if (e?.defaultPrevented) return;
                                    if (!isBubbleActionEligible(msg) || msg.id == null) return;
                                    toggleSelect(msg.id);
                                  };
                                } else if (msg.isSpecialTemplateMessage && isTemplateExpanded) {
                                  handlers.onClick = (e) => {
                                    lp.onClick(e);
                                    setExpandedTemplates((prev) => {
                                      const next = new Set(prev);
                                      next.delete(msg.id);
                                      return next;
                                    });
                                  };
                                } else {
                                  handlers.onClick = lp.onClick;
                                }
                                return handlers;
                              })()}
                            >
                              {msg.isRevoked && (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontStyle: 'italic',
                                    color: 'rgba(0,0,0,0.45)',
                                    userSelect: 'none',
                                  }}
                                >
                                  This message was deleted
                                </Typography>
                              )}
                              {!msg.isRevoked && msg?.repliedTo && (
                                <Box
                                  onClick={() => scrollToMessage(msg.repliedTo.id)}
                                  sx={{
                                    display: 'flex',
                                    gap: '6px',
                                    alignItems: 'stretch',
                                    marginBottom: '6px',
                                    padding: '6px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderLeft: `3px solid ${COLORS.primary}`,
                                    cursor: msg.repliedTo.id != null ? 'pointer' : 'default',
                                    maxWidth: '100%',
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: COLORS.primary,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {msg.repliedTo.isFromMe
                                        ? 'You'
                                        : whatsAppChatMetaData?.name || whatsAppChatMetaData?.phoneNumber || 'Contact'}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: '13px',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        lineHeight: 1.3,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {msg.repliedTo.body ||
                                        (msg.repliedTo.messageType !== 'text' ? `[${msg.repliedTo.messageType}]` : '')}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              {/* Track D / W-1 — "↪ Forwarded" badge. Sits above
                                                        the body; keeps the layout in sync with native
                                                        WA so operators recognise it instantly. */}
                              {!msg.isRevoked && msg?.forwardedFrom && (
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                    color: 'rgba(0,0,0,0.45)',
                                    marginBottom: '4px',
                                    userSelect: 'none',
                                  }}
                                >
                                  ↪ Forwarded
                                </Typography>
                              )}
                              {!msg.isRevoked && msg?.mediaMessage && (
                                <MediaDisplayComponent
                                  scrollToBottom={() => {}}
                                  whatsAppMessageMedia={msg?.mediaMessage ?? {}}
                                  messageType={msg?.mediaMessageType}
                                />
                              )}
                              {!msg.isRevoked &&
                                (msg.content || msg?.mediaMessage?.whatsapp_media_caption) &&
                                editingBubble?.wamid &&
                                msg.wamid &&
                                editingBubble.wamid === msg.wamid && (
                                  // Track C / F6 inline editor — bubble body becomes a
                                  // multiline TextField with Save / Cancel. Cmd/Ctrl+Enter
                                  // submits, Esc cancels. Saving stays in slice (optimistic
                                  // body swap + revert) — we close the editor immediately so
                                  // the operator sees the updated bubble without a flash.
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                      autoFocus
                                      multiline
                                      minRows={1}
                                      maxRows={6}
                                      fullWidth
                                      size="small"
                                      value={editingBubble.draft}
                                      onChange={(e) =>
                                        setEditingBubble((prev) => (prev ? { ...prev, draft: e.target.value } : prev))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          cancelEdit();
                                        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                          e.preventDefault();
                                          submitEdit();
                                        }
                                      }}
                                      aria-label="Edit message"
                                      inputProps={{ 'aria-label': 'Edit message body' }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'rgba(255,255,255,0.6)',
                                          fontSize: '14px',
                                          lineHeight: 1.4,
                                        },
                                      }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                      <Button
                                        size="small"
                                        onClick={cancelEdit}
                                        sx={{ textTransform: 'none', fontSize: '12px' }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={submitEdit}
                                        disabled={!(editingBubble.draft || '').trim()}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '12px',
                                          backgroundColor: COLORS.primary,
                                          '&:hover': { backgroundColor: COLORS.primaryDark },
                                        }}
                                      >
                                        Save
                                      </Button>
                                    </Box>
                                  </Box>
                                )}
                              {!msg.isRevoked &&
                                (msg.content || msg?.mediaMessage?.whatsapp_media_caption) &&
                                !(editingBubble?.wamid && msg.wamid && editingBubble.wamid === msg.wamid) && (
                                  <Typography component="span" sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: parseWhatsappMarkup(
                                          msg.content || msg?.mediaMessage?.whatsapp_media_caption
                                        ),
                                      }}
                                    />
                                  </Typography>
                                )}
                              {msg.isSpecialTemplateMessage && (
                                <Tooltip title="Automated template sent to patient by the system" arrow>
                                  <Box sx={styles.templateIndicator}>
                                    <Icon
                                      icon="mdi:robot-outline"
                                      width={14}
                                      height={14}
                                      style={{ color: COLORS.primary }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                              <Box component="span" sx={styles.messageMetaInline}>
                                {msg.isEdited && !msg.isRevoked && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '11px',
                                      color: 'rgba(0,0,0,0.4)',
                                      fontStyle: 'italic',
                                      ml: 0.5,
                                    }}
                                  >
                                    (edited)
                                  </Typography>
                                )}
                                {msg.isStarred && !msg.isRevoked && (
                                  <Tooltip title="Starred" arrow>
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}
                                      aria-label="Starred message"
                                    >
                                      <Icon icon="mdi:star" width={12} height={12} style={{ color: COLORS.warning }} />
                                    </Box>
                                  </Tooltip>
                                )}
                                {msg.timestamp && (
                                  <Typography component="span" sx={styles.messageTimeInline}>
                                    {msg.timestamp}
                                  </Typography>
                                )}
                                {msg.type === 'sent' && (
                                  <StatusIcon status={msg.status} updatedTimestamp={msg.updatedTimestamp} />
                                )}
                              </Box>
                              {msg.emoji && <Box sx={styles.reaction}>{msg.emoji}</Box>}
                            </Box>
                          </Box>
                        </Box>

                        {/* Interactive Message Data Pills */}
                        {msg?.isMessageInteractive && (
                          <Box sx={styles.dataPills}>
                            {msg?.flowResponseEmail && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillEmail }}>
                                <Icon icon="mdi:email-outline" width={14} height={14} />
                                {msg?.flowResponseEmail}
                              </Box>
                            )}
                            {msg?.flowResponseTreatment && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillTreatment }}>
                                <Icon icon="mdi:tooth-outline" width={14} height={14} />
                                {msg?.flowResponseTreatment}
                              </Box>
                            )}
                            {msg?.flowResponseComment && (
                              <Box sx={{ ...styles.dataPill, ...styles.dataPillComment }}>
                                <Icon icon="mdi:comment-outline" width={14} height={14} />
                                {msg?.flowResponseComment}
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Tier 3 / F-33 — first-unread divider.
                                            Rendered between the message and the date divider in
                                            JSX; column-reverse places it visually above the
                                            boundary message and below the date label. */}
                        {index === unreadDividerIndex && unreadDividerCount > 0 && (
                          <UnreadDivider ref={unreadDividerRef} count={unreadDividerCount} />
                        )}
                        {/* Date Divider - Rendered after message, appears above due to column-reverse */}
                        {dateDivider && (
                          <Box sx={styles.dateDivider}>
                            <Box component="span" sx={styles.dateDividerText}>
                              {dateDivider}
                            </Box>
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </InfiniteScroll>
            </Box>
          )}

          {/* Template preview — shown as outgoing chat bubble before sending */}
          {templatePreview && (
            <Box sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={styles.templatePreviewBubble}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                  <Icon icon="mdi:file-document-outline" width={14} height={14} style={{ color: COLORS.primary }} />
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: COLORS.textPrimary }}>
                    {formatTemplateName(templatePreview.name)}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: '13px', color: COLORS.textBody, lineHeight: 1.6, whiteSpace: 'pre-wrap', mb: '10px' }}
                >
                  {getTemplateBody(templatePreview) || 'No body text'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${COLORS.dividerMedium}`,
                    pt: '8px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setTemplatePreview(null)}
                    sx={{ textTransform: 'none', fontSize: '12px', color: COLORS.textSecondary, minWidth: 'auto' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      sendWhatsappMessageTemplate(templatePreview);
                      setTemplatePreview(null);
                      setTemplateSearch('');
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      backgroundColor: COLORS.primary,
                      '&:hover': { backgroundColor: COLORS.primaryDark },
                      minWidth: 'auto',
                      padding: '4px 14px',
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Attachment preview — shown as outgoing chat bubble before sending */}
          {attachmentFile && (
            <Box sx={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={styles.attachmentPreviewBubble}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                  <Icon icon="mdi:attachment" width={14} height={14} style={{ color: COLORS.primary }} />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attachmentFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: COLORS.textDisabled, flexShrink: 0, ml: 'auto' }}>
                    {(attachmentFile.size / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
                {attachmentPreview && attachmentFile.type?.startsWith('image/') && (
                  <Box
                    component="img"
                    src={attachmentPreview}
                    alt={attachmentFile.name}
                    sx={styles.attachmentThumbnail}
                  />
                )}
                {attachmentPreview && attachmentFile.type?.startsWith('video/') && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      p: '8px',
                      backgroundColor: COLORS.dividerLighter,
                      borderRadius: '6px',
                    }}
                  >
                    <Icon icon="mdi:video-outline" width={20} height={20} style={{ color: COLORS.textSecondary }} />
                    <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      {attachmentFile.name}
                    </Typography>
                  </Box>
                )}
                {!attachmentPreview && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      p: '8px',
                      backgroundColor: COLORS.dividerLighter,
                      borderRadius: '6px',
                    }}
                  >
                    <Icon icon="mdi:file-outline" width={20} height={20} style={{ color: COLORS.textSecondary }} />
                    <Typography sx={{ fontSize: '12px', color: COLORS.textSecondary }}>
                      {attachmentFile.name}
                    </Typography>
                  </Box>
                )}
                <Box
                  component="input"
                  placeholder="Add a caption..."
                  value={attachmentCaption}
                  onChange={(e) => setAttachmentCaption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendAttachment();
                    }
                  }}
                  sx={styles.attachmentCaptionInput}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${COLORS.dividerMedium}`,
                    pt: '8px',
                    mt: '8px',
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleCancelAttachment}
                    sx={{ textTransform: 'none', fontSize: '12px', color: COLORS.textSecondary, minWidth: 'auto' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSendAttachment}
                    disabled={whatsppSendLoading || !isSendAllowed || isWindowExpired}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      backgroundColor: COLORS.primary,
                      '&:hover': { backgroundColor: COLORS.primaryDark },
                      minWidth: 'auto',
                      padding: '4px 14px',
                    }}
                  >
                    {whatsppSendLoading ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Hidden file input for attachments. The `accept` attribute is
                re-scoped at click time by triggerFilePicker() based on which
                option the user chose in the + popover (Photos/Videos vs
                Document). Default accept matches the union of both. */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,video/mp4,video/3gpp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Attachment "+" popover — native-WA-style coloured shortcuts.
                Only renders options whose underlying send path is wired up.
                Currently: Photos & Videos (images+video) and Document. */}
          <Popover
            open={Boolean(attachmentMenuAnchor)}
            anchorEl={attachmentMenuAnchor}
            onClose={closeAttachmentMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { ...styles.attachmentPopoverPaper, mt: '-8px' } } }}
          >
            <Box sx={{ py: 0.5 }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => triggerFilePicker('image/jpeg,image/png,video/mp4,video/3gpp')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    triggerFilePicker('image/jpeg,image/png,video/mp4,video/3gpp');
                }}
                sx={styles.attachmentPopoverItem}
              >
                <Box sx={{ ...styles.attachmentPopoverIconWrap, backgroundColor: '#3B82F6' }}>
                  <Icon icon="mdi:image-outline" width={20} height={20} />
                </Box>
                <Box sx={styles.attachmentPopoverTextCol}>
                  <Typography sx={styles.attachmentPopoverLabel}>Photos &amp; Videos</Typography>
                  <Typography sx={styles.attachmentPopoverSublabel}>JPEG, PNG, MP4</Typography>
                </Box>
              </Box>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => triggerFilePicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    triggerFilePicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt');
                }}
                sx={styles.attachmentPopoverItem}
              >
                <Box sx={{ ...styles.attachmentPopoverIconWrap, backgroundColor: '#A855F7' }}>
                  <Icon icon="mdi:file-document-outline" width={20} height={20} />
                </Box>
                <Box sx={styles.attachmentPopoverTextCol}>
                  <Typography sx={styles.attachmentPopoverLabel}>Document</Typography>
                  <Typography sx={styles.attachmentPopoverSublabel}>PDF, DOC, XLS, PPT, TXT</Typography>
                </Box>
              </Box>
            </Box>
          </Popover>

          {/* Window expired banner — above footer, inside conversation area. */}
          {isWindowExpired && (
            <Box
              sx={{
                ...styles.windowStatus,
                ...styles.windowStatusExpired,
                borderTop: `1px solid ${COLORS.divider}`,
                marginBottom: 0,
                padding: '6px 16px',
              }}
            >
              <Icon icon="mdi:clock-outline" width={14} height={14} />
              <Typography component="span" sx={{ fontSize: '11px' }}>
                24-hour window closed • Only templates can be delivered.
              </Typography>
            </Box>
          )}

          {/* Scroll to bottom FAB — rendered as a sibling of the footer so
                it anchors to the ChatPage root's relative box and hovers at
                the bottom-right of the chat pane (native-WA / iMessage feel). */}
          {!isUserNearBottom && !whatsappChatScreenLoading && whatsappMessages?.length > 0 && (
            <Box
              onClick={scrollToBottom}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') scrollToBottom();
              }}
              role="button"
              tabIndex={0}
              aria-label={
                unreadWhileScrolled > 0 ? `Scroll to latest — ${unreadWhileScrolled} new` : 'Scroll to latest'
              }
              sx={styles.scrollToBottomBtn}
            >
              <Icon icon="mdi:chevron-down" width={22} height={22} />
              {unreadWhileScrolled > 0 && (
                <Box component="span" sx={styles.scrollToBottomBtnBadge}>
                  {unreadWhileScrolled > 99 ? '99+' : unreadWhileScrolled}
                </Box>
              )}
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ ...styles.footer, ...(isMobile && styles.footerMobile) }}>
            {isWindowExpired ? (
              /* Template Command Centre */
              <Box sx={{ position: 'relative', width: '100%' }}>
                {embedded ? (
                  /* Compact template bar for embedded/sidebar mode */
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                      onClick={() => setTemplateModalOpen(true)}
                      variant="contained"
                      size="small"
                      startIcon={<Icon icon="mdi:file-document-outline" width={14} height={14} />}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        lineHeight: 1.2,
                        minHeight: 0,
                        backgroundColor: 'primary.main',
                        color: '#fff',
                        boxShadow: 'none',
                        px: '12px',
                        py: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Send Template
                    </Button>
                  </Box>
                ) : (
                  <Box sx={styles.templateInputContainer}>
                    <Button onClick={() => setTemplateModalOpen(true)} sx={styles.templateSelectBtn} size="small">
                      <Icon icon="mdi:file-document-outline" width={14} height={14} style={{ color: COLORS.primary }} />
                      Browse All
                    </Button>
                    <Box
                      component="input"
                      placeholder="Search templates..."
                      value={templateSearch}
                      onChange={(e) => {
                        setTemplateSearch(e.target.value);
                        setTemplateDropdownOpen(true);
                      }}
                      onFocus={() => setTemplateDropdownOpen(true)}
                      sx={styles.templateSearchInput}
                    />
                  </Box>
                )}
                {/* Template search results dropdown — full-screen only */}
                {!embedded && templateDropdownOpen && !templatePreview && (
                  <>
                    <Box
                      sx={{ position: 'fixed', inset: 0, zIndex: 999 }}
                      onClick={() => setTemplateDropdownOpen(false)}
                    />
                    <Box sx={styles.templateDropdown}>
                      {(() => {
                        const results = (filteredTemplates || []).filter(
                          (t) =>
                            !templateSearch ||
                            matchesSearch(t.name, templateSearch) ||
                            getTemplateBody(t)?.toLowerCase()?.includes(templateSearch.toLowerCase().trim())
                        );
                        if (results.length === 0)
                          return (
                            <Box
                              sx={{
                                padding: '16px',
                                textAlign: 'center',
                                color: COLORS.textDisabled,
                                fontSize: '13px',
                              }}
                            >
                              No templates found
                            </Box>
                          );
                        return results.slice(0, 6).map((t) => (
                          <Box
                            key={t.id}
                            onClick={() => {
                              setTemplateDropdownOpen(false);
                              setTemplateSearch('');
                              setTemplatePreview(t);
                            }}
                            sx={styles.templateDropdownItem}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                              <Icon
                                icon="mdi:file-document-outline"
                                width={14}
                                height={14}
                                style={{ color: COLORS.primary, flexShrink: 0 }}
                              />
                              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.textPrimary }}>
                                {formatTemplateName(t.name)}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: COLORS.textSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                pl: '20px',
                              }}
                            >
                              {getTemplateBody(t)?.slice(0, 100) || 'No body text'}
                            </Typography>
                          </Box>
                        ));
                      })()}
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* Normal Message Mode - disabled while loading */
              <>
                {/* AI Result Panel */}
                <FeatureGuard feature="whatsapp-ai" level="view">
                  <AIResultPanel
                    loading={aiAssistantLoading}
                    result={aiAssistantResult}
                    error={aiAssistantError}
                    actionType={aiAssistantActionType}
                    onClose={handleAiResultClose}
                    onSend={handleAiResultSend}
                    onRegenerate={handleAiRegenerate}
                  />
                </FeatureGuard>

                {/* Compact toolbar row — only in embedded (sidebar) mode */}
                {embedded && (
                  <Box sx={styles.embeddedToolbarRow}>
                    <FormattingToolbar textareaRef={inputRef} value={sendMessage} onChange={setSendMessage} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: 'auto' }}>
                      <EmojiPickerButton
                        onEmojiSelect={handleEmojiSelect}
                        disabled={
                          whatsappChatScreenLoading ||
                          whatsappChats === undefined ||
                          whatsappChats === null ||
                          whatsppSendLoading ||
                          !isSendAllowed
                        }
                      />
                      <Tooltip title="Attach" arrow>
                        <IconButton
                          onClick={openAttachmentMenu}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                          sx={styles.inputBtn}
                        >
                          <Icon icon="mdi:plus-circle-outline" width={20} height={20} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send a template message" arrow>
                        <IconButton
                          onClick={() => setTemplateModalOpen(true)}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                          sx={styles.inputBtn}
                        >
                          <Icon icon="mdi:file-document-outline" width={18} height={18} />
                        </IconButton>
                      </Tooltip>
                      {isAiEnabled && (
                        <FeatureGuard feature="whatsapp-ai" level="view">
                          <AIAssistantButton
                            onClick={handleAiButtonClick}
                            isActive={aiMenuOpen}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              aiAssistantLoading ||
                              whatsppSendLoading ||
                              !isSendAllowed
                            }
                          />
                        </FeatureGuard>
                      )}
                    </Box>
                  </Box>
                )}
                {/* Reply-to-quote preview — sits above the composer when the
                            operator picks "Reply" on a bubble. Mirrors native WA: a green
                            left-bar accent, sender label, single-line body preview, and a
                            close button that clears the active reply.
                            Track D / F9: framer-motion slide-up animation on appear/dismiss. */}
                <AnimatePresence initial={false}>
                  {replyingTo && (
                    <m.div
                      key="composer-quote-preview"
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 10, opacity: 0 }}
                      transition={{ duration: 0.15, ease: [0.2, 0.9, 0.3, 1] }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          margin: '6px 12px 0 12px',
                          padding: '8px 10px',
                          backgroundColor: COLORS.primaryLighter,
                          borderRadius: '8px',
                          borderLeft: `3px solid ${COLORS.primary}`,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: COLORS.primary,
                              lineHeight: 1.2,
                            }}
                          >
                            {replyingTo.isFromMe
                              ? 'You'
                              : whatsAppChatMetaData?.name || whatsAppChatMetaData?.phoneNumber || 'Contact'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: COLORS.textSecondary,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {replyingTo.body || 'Media'}
                          </Typography>
                        </Box>
                        <Tooltip title="Cancel reply" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setReplyingTo(null)}
                            sx={{
                              color: COLORS.textSecondary,
                              '&:hover': { color: COLORS.textPrimary },
                            }}
                          >
                            <Icon icon="mdi:close" width={16} height={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </m.div>
                  )}
                </AnimatePresence>
                <Box sx={embedded ? styles.embeddedInputContainer : styles.inputContainer}>
                  {isMobile && !embedded ? (
                    <>
                      {/* Mobile Row 1: textarea + send */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <textarea
                          ref={inputRef}
                          value={sendMessage}
                          rows={1}
                          placeholder={(() => {
                            if (!isSendAllowed)
                              return threadHasLead && !leadDetailsLoaded
                                ? 'Loading lead details...'
                                : 'Lead is not available for communication';
                            if (whatsppSendLoading) return 'Sending...';
                            if (whatsappChatScreenLoading) return 'Loading...';
                            return 'Type your message...';
                          })()}
                          style={{
                            ...styles.inputBox,
                            ...(whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                              ? { opacity: 0.5, cursor: 'not-allowed' }
                              : {}),
                          }}
                          onChange={handleSendMessageChange}
                          onKeyUp={(evt) => {
                            // F-20/21/22 — re-evaluate the autocomplete trigger
                            // on caret moves (arrow keys, click) that don't fire
                            // onChange.
                            composerAutocomplete.evaluate(
                              sendMessage,
                              evt?.target?.selectionStart ?? sendMessage.length
                            );
                          }}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                          onKeyDown={(evt) => {
                            // F-20/21/22 — autocomplete intercepts arrow keys,
                            // Enter, and Escape when a popover is open. Falls
                            // through to formatting / send shortcuts otherwise.
                            if (handleAutocompleteKeyDown(evt)) return;
                            // 2026-05-06 P0 — held-Enter on macOS auto-repeats
                            // every ~30 ms, firing send twice before the loading
                            // flag flips. evt.repeat catches OS-level repeats;
                            // isComposing skips IME composition Enter.
                            if (evt.key === 'Enter' && !evt.shiftKey && !evt.repeat && !evt.nativeEvent?.isComposing) {
                              evt.preventDefault();
                              sendWhatsappMessageHandler(sendMessage);
                              return;
                            }
                            if (evt.key === 'Enter') {
                              // Eat repeat / IME Enters so the textarea doesn't insert a newline
                              evt.preventDefault();
                              return;
                            }
                            const isMod = evt.metaKey || evt.ctrlKey;
                            if (isMod && evt.key === 'b') {
                              evt.preventDefault();
                              applyFormatting('*');
                            } else if (isMod && evt.key === 'i') {
                              evt.preventDefault();
                              applyFormatting('_');
                            } else if (isMod && evt.shiftKey && (evt.key === 'x' || evt.key === 'X')) {
                              evt.preventDefault();
                              applyFormatting('~');
                            } else if (isMod && evt.shiftKey && (evt.key === 'm' || evt.key === 'M')) {
                              evt.preventDefault();
                              applyFormatting('```');
                            }
                          }}
                          onPaste={(evt) => {
                            // F-17 — image clipboard intercept. If we attached an
                            // image we're done; otherwise let the textarea handle
                            // plain-text paste natively. onChange will fire with
                            // the merged value and `handleSendMessageChange`
                            // updates both `setSendMessage` and `persistDraft`.
                            // Calling preventDefault here would suppress native
                            // undo history and break accessibility tooling that
                            // hooks the paste event.
                            handleComposerPaste(evt);
                          }}
                        />
                        {sendMessage.trim().length > 0 && (
                          <IconButton
                            onClick={() => sendWhatsappMessageHandler(sendMessage)}
                            sx={styles.sendBtn}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              whatsppSendLoading ||
                              !isSendAllowed
                            }
                          >
                            <Icon icon="mdi:send" width={18} height={18} />
                          </IconButton>
                        )}
                      </Box>
                      {/* Mobile Row 2: action buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Tooltip title="Attach" arrow>
                          <IconButton
                            onClick={openAttachmentMenu}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              whatsppSendLoading ||
                              !isSendAllowed
                            }
                            sx={styles.inputBtn}
                          >
                            <Icon icon="mdi:plus-circle-outline" width={22} height={22} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Browse & manage templates" arrow>
                          <IconButton
                            onClick={() => setTemplateModalOpen(true)}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              whatsppSendLoading
                            }
                            sx={styles.inputBtn}
                            aria-label="Browse and manage WhatsApp templates"
                          >
                            <Icon icon="mdi:file-document-multiple-outline" width={22} height={22} />
                          </IconButton>
                        </Tooltip>
                        <EmojiPickerButton
                          onEmojiSelect={handleEmojiSelect}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                          isMobile={isMobile}
                        />
                        <IconButton
                          sx={styles.inputBtn}
                          onClick={() => setTemplateModalOpen(true)}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                        >
                          <Icon icon="mdi:file-document-outline" width={20} height={20} />
                        </IconButton>
                        <FormattingToolbar
                          textareaRef={inputRef}
                          value={sendMessage}
                          onChange={setSendMessage}
                          isMobile={isMobile}
                        />
                        {isAiEnabled && (
                          <FeatureGuard feature="whatsapp-ai" level="view">
                            <AIAssistantButton
                              onClick={handleAiButtonClick}
                              isActive={aiMenuOpen}
                              disabled={
                                whatsappChatScreenLoading ||
                                whatsappChats === undefined ||
                                whatsappChats === null ||
                                aiAssistantLoading ||
                                whatsppSendLoading ||
                                !isSendAllowed
                              }
                            />
                          </FeatureGuard>
                        )}
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Desktop layout */}
                      {!embedded && (
                        <>
                          {isCompact ? (
                            // Compact width — icon-only Template button to reclaim
                            // horizontal space for the textarea.
                            <Tooltip title="Send a template message" arrow>
                              <IconButton
                                onClick={() => setTemplateModalOpen(true)}
                                disabled={
                                  whatsappChatScreenLoading ||
                                  whatsappChats === undefined ||
                                  whatsappChats === null ||
                                  whatsppSendLoading ||
                                  !isSendAllowed
                                }
                                sx={styles.inputBtn}
                              >
                                <Icon icon="mdi:file-document-outline" width={20} height={20} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Button
                              sx={styles.templateBtn}
                              onClick={() => setTemplateModalOpen(true)}
                              disabled={
                                whatsappChatScreenLoading ||
                                whatsappChats === undefined ||
                                whatsappChats === null ||
                                whatsppSendLoading ||
                                !isSendAllowed
                              }
                            >
                              <Icon icon="mdi:file-document-outline" width={16} height={16} />
                              Template
                            </Button>
                          )}
                          <EmojiPickerButton
                            onEmojiSelect={handleEmojiSelect}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              whatsppSendLoading ||
                              !isSendAllowed
                            }
                          />
                          <Tooltip title="Attach" arrow>
                            <IconButton
                              onClick={openAttachmentMenu}
                              disabled={
                                whatsappChatScreenLoading ||
                                whatsappChats === undefined ||
                                whatsappChats === null ||
                                whatsppSendLoading ||
                                !isSendAllowed
                              }
                              sx={styles.inputBtn}
                            >
                              <Icon icon="mdi:plus-circle-outline" width={22} height={22} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Browse & manage templates" arrow>
                            <IconButton
                              onClick={() => setTemplateModalOpen(true)}
                              disabled={
                                whatsappChatScreenLoading ||
                                whatsappChats === undefined ||
                                whatsappChats === null ||
                                whatsppSendLoading
                              }
                              sx={styles.inputBtn}
                              aria-label="Browse and manage WhatsApp templates"
                            >
                              <Icon icon="mdi:file-document-multiple-outline" width={22} height={22} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <textarea
                        ref={inputRef}
                        value={sendMessage}
                        rows={1}
                        placeholder={(() => {
                          if (!isSendAllowed)
                            return threadHasLead && !leadDetailsLoaded
                              ? 'Loading lead details...'
                              : 'Lead is not available for communication';
                          if (whatsppSendLoading) return 'Sending...';
                          if (whatsappChatScreenLoading) return 'Loading...';
                          return 'Type your message...';
                        })()}
                        style={{
                          ...styles.inputBox,
                          ...(whatsappChatScreenLoading ||
                          whatsappChats === undefined ||
                          whatsappChats === null ||
                          whatsppSendLoading ||
                          !isSendAllowed
                            ? { opacity: 0.5, cursor: 'not-allowed' }
                            : {}),
                        }}
                        onChange={handleSendMessageChange}
                        onKeyUp={(evt) => {
                          composerAutocomplete.evaluate(sendMessage, evt?.target?.selectionStart ?? sendMessage.length);
                        }}
                        disabled={
                          whatsappChatScreenLoading ||
                          whatsappChats === undefined ||
                          whatsappChats === null ||
                          whatsppSendLoading ||
                          !isSendAllowed
                        }
                        onKeyDown={(evt) => {
                          if (handleAutocompleteKeyDown(evt)) return;
                          // 2026-05-06 P0 — repeat / IME Enter must not fire send.
                          if (evt.key === 'Enter' && !evt.shiftKey && !evt.repeat && !evt.nativeEvent?.isComposing) {
                            evt.preventDefault();
                            sendWhatsappMessageHandler(sendMessage);
                            return;
                          }
                          if (evt.key === 'Enter') {
                            evt.preventDefault();
                            return;
                          }
                          const isMod = evt.metaKey || evt.ctrlKey;
                          if (isMod && evt.key === 'b') {
                            evt.preventDefault();
                            applyFormatting('*');
                          } else if (isMod && evt.key === 'i') {
                            evt.preventDefault();
                            applyFormatting('_');
                          } else if (isMod && evt.shiftKey && (evt.key === 'x' || evt.key === 'X')) {
                            evt.preventDefault();
                            applyFormatting('~');
                          } else if (isMod && evt.shiftKey && (evt.key === 'm' || evt.key === 'M')) {
                            evt.preventDefault();
                            applyFormatting('```');
                          }
                        }}
                        onPaste={(evt) => {
                          // F-17 — see desktop variant. Image consumed by the
                          // helper if present; plain text falls through to the
                          // textarea's native paste so undo history + a11y hooks
                          // keep working.
                          handleComposerPaste(evt);
                        }}
                      />
                      {!embedded && (
                        <FormattingToolbar
                          textareaRef={inputRef}
                          value={sendMessage}
                          onChange={setSendMessage}
                          isCompact={isCompact}
                        />
                      )}
                      {!embedded && isAiEnabled && (
                        <FeatureGuard feature="whatsapp-ai" level="view">
                          <AIAssistantButton
                            onClick={handleAiButtonClick}
                            isActive={aiMenuOpen}
                            disabled={
                              whatsappChatScreenLoading ||
                              whatsappChats === undefined ||
                              whatsappChats === null ||
                              aiAssistantLoading ||
                              whatsppSendLoading ||
                              !isSendAllowed
                            }
                          />
                        </FeatureGuard>
                      )}
                      {sendMessage.trim().length > 0 && (
                        <IconButton
                          onClick={() => sendWhatsappMessageHandler(sendMessage)}
                          sx={styles.sendBtn}
                          disabled={
                            whatsappChatScreenLoading ||
                            whatsappChats === undefined ||
                            whatsappChats === null ||
                            whatsppSendLoading ||
                            !isSendAllowed
                          }
                        >
                          <Icon icon="mdi:send" width={18} height={18} />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>

                {/* AI Assistant Menu */}
                {isAiEnabled && (
                  <FeatureGuard feature="whatsapp-ai" level="view">
                    <AIAssistantMenu
                      anchorEl={aiMenuAnchor}
                      open={aiMenuOpen}
                      onClose={handleAiMenuClose}
                      onSelectAction={handleAiSelectAction}
                      loading={aiAssistantLoading}
                      isMobile={isMobile}
                    />
                  </FeatureGuard>
                )}

                {/* Tier 2 / F-20, F-21, F-22 — Composer autocomplete popover.
                            Anchored to the textarea; one component handles all three
                            triggers because the items / labels / selection logic is
                            piped in from the trigger-specific memos above. */}
                <ComposerAutocompletePopover
                  open={Boolean(composerAutocomplete.trigger) && activeAutocompleteItems.length > 0}
                  anchorEl={inputRef.current}
                  items={activeAutocompleteItems}
                  selectedIndex={composerAutocomplete.selectedIndex}
                  onIndexChange={composerAutocomplete.setSelectedIndex}
                  onSelect={(item) => applyAutocompleteSelection(item)}
                  onClose={composerAutocomplete.close}
                  getKey={(item, idx) => {
                    if (composerAutocomplete.trigger === '/') return item?.id ?? item?.name ?? idx;
                    if (composerAutocomplete.trigger === ':') return item?.emoji + idx;
                    return item;
                  }}
                  getLabel={renderAutocompleteLabel}
                  getDescription={renderAutocompleteDescription}
                  placement="top-start"
                  emptyLabel={(() => {
                    if (composerAutocomplete.trigger === '/') return 'No matching templates';
                    if (composerAutocomplete.trigger === ':') return 'No matching emoji';
                    return 'No matching merge tags';
                  })()}
                  footerAction={
                    composerAutocomplete.trigger === '/'
                      ? {
                          label: 'Browse templates',
                          icon: 'mdi:file-document-multiple-outline',
                          onClick: () => {
                            composerAutocomplete.close();
                            setTemplateModalOpen(true);
                          },
                        }
                      : null
                  }
                />
              </>
            )}
          </Box>
        </>
      }

      {/* Template Modal */}
      <WhatsappTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        currentIndex={currentIndex}
        templates={filteredTemplates}
        onNext={() => setCurrentIndex((i) => Math.min(i + 1, filteredTemplates.length - 1))}
        onPrevious={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
        onSelect={setCurrentIndex}
        onSend={() => sendWhatsappMessageTemplate(filteredTemplates[currentIndex] ?? {})}
      />

      {/* Close Conversation Popup */}
      <CloseChatPopup
        open={openCloseConversation}
        onClose={handleCancelCloseConversation}
        onConfirmClose={handleConfirmCloseConversation}
        contactDetails={{
          name: whatsAppChatMetaData?.thread?.contact_name,
          leadDetails: whatsAppChatMetaData?.leadDetails,
        }}
        practiceId={practiceId}
        practiceChoices={practiceChoices}
      />

      {/* Bubble action sheet — Reply + Copy + Forward today. New
                Tier-1 features (react, star, edit, pin, delete, info) plug
                in via the `slots` prop without rewiring the trigger logic.
                Track C: Edit visible only when the message is outbound text
                inside the 15-minute Meta window (D8 — silent gating, no
                countdown banner). Info visible on every eligible bubble. */}
      <MessageActionSheet
        open={Boolean(bubbleMenu)}
        anchorEl={bubbleMenu?.anchorEl ?? null}
        message={bubbleMenu?.msg ?? null}
        onClose={closeBubbleMenu}
        slots={{
          reactionBar: bubbleMenu?.msg ? (
            <EmojiReactionBar
              currentEmoji={bubbleMenu?.msg?.emoji || null}
              onPick={handleReactionPick}
              onRemove={handleReactionRemove}
            />
          ) : null,
          primaryActions: [
            <MenuItem
              key="reply"
              role="menuitem"
              onClick={() => handleReplyBubble()}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:reply-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Reply
            </MenuItem>,
            <MenuItem
              key="copy"
              role="menuitem"
              onClick={handleCopyBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:content-copy" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Copy
            </MenuItem>,
            <MenuItem
              key="forward"
              role="menuitem"
              onClick={handleForwardBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:share-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Forward
            </MenuItem>,
            <MenuItem
              key="star"
              role="menuitem"
              onClick={handleStarBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon
                icon={bubbleMenu?.msg?.isStarred ? 'mdi:star' : 'mdi:star-outline'}
                width={16}
                height={16}
                style={{ color: bubbleMenu?.msg?.isStarred ? COLORS.warning : COLORS.textSecondary }}
              />
              {bubbleMenu?.msg?.isStarred ? 'Unstar' : 'Star'}
            </MenuItem>,
            <MenuItem
              key="pin"
              role="menuitem"
              onClick={handlePinBubble}
              disabled={Boolean(bubbleMenu?.msg?.isRevoked)}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:pin-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Pin
            </MenuItem>,
            // Edit only renders for outbound messages within the
            // 15-min Meta edit window AND only when not in
            // multi-select mode (single-bubble action only).
            !isSelecting && isBubbleEditEligible(bubbleMenu?.msg) ? (
              <MenuItem
                key="edit"
                role="menuitem"
                onClick={handleEditBubble}
                sx={{
                  fontSize: '13px',
                  py: 0.9,
                  px: 1.75,
                  gap: 1.25,
                  color: COLORS.textPrimary,
                  '&:hover': { backgroundColor: COLORS.primaryLighter },
                }}
              >
                <Icon icon="mdi:pencil-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
                Edit
              </MenuItem>
            ) : null,
            <MenuItem
              key="info"
              role="menuitem"
              onClick={handleInfoBubble}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon icon="mdi:information-outline" width={16} height={16} style={{ color: COLORS.textSecondary }} />
              Info
            </MenuItem>,
            <MenuItem
              key="select"
              role="menuitem"
              onClick={() => {
                const msg = bubbleMenu?.msg;
                closeBubbleMenu();
                if (msg?.id != null && isBubbleActionEligible(msg)) toggleSelect(msg.id);
              }}
              sx={{
                fontSize: '13px',
                py: 0.9,
                px: 1.75,
                gap: 1.25,
                color: COLORS.textPrimary,
                '&:hover': { backgroundColor: COLORS.primaryLighter },
              }}
            >
              <Icon
                icon="mdi:checkbox-multiple-marked-outline"
                width={16}
                height={16}
                style={{ color: COLORS.textSecondary }}
              />
              Select
            </MenuItem>,
          ],
          destructiveActions:
            bubbleMenu?.msg?.type === 'sent'
              ? [
                  <MenuItem
                    key="delete"
                    role="menuitem"
                    onClick={handleDeleteBubble}
                    sx={{
                      fontSize: '13px',
                      py: 0.9,
                      px: 1.75,
                      gap: 1.25,
                      color: COLORS.error || '#d32f2f',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                    }}
                  >
                    <Icon
                      icon="mdi:delete-outline"
                      width={16}
                      height={16}
                      style={{ color: COLORS.error || '#d32f2f' }}
                    />
                    Delete from CRM
                  </MenuItem>,
                ]
              : null,
        }}
      />

      {/* Delete-for-everyone confirmation dialog. Native WA shows an
                inline action sheet; we use MUI Dialog to match the rest of the
                CRM and so destructive intent is unmistakable. */}
      <Dialog
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Delete from CRM?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody, mb: 1 }}>
            This message will be hidden in the CRM and replaced with a &ldquo;deleted&rdquo; placeholder. This cannot be
            undone.
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.textMuted || 'rgba(0,0,0,0.55)', fontStyle: 'italic' }}>
            Note: WhatsApp&rsquo;s Cloud API does not currently expose delete-for-everyone. The patient&rsquo;s phone
            will still show the original message.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} sx={{ fontSize: 13 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRevoke} color="error" variant="contained" sx={{ fontSize: 13 }}>
            Delete from CRM
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog for replacing an existing pin (D5 — max 1
                pin per thread; new pin overwrites old). */}
      <Dialog
        open={Boolean(replacePinTarget)}
        onClose={() => setReplacePinTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontSize: 16 }}>Replace pinned message?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody }}>
            Only one message can be pinned at a time. Pinning this message will replace the current pin.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReplacePinTarget(null)}
            size="small"
            sx={{ textTransform: 'none', color: COLORS.textSecondary }}
          >
            Cancel
          </Button>
          <Button onClick={confirmReplacePin} size="small" variant="contained" sx={{ textTransform: 'none' }}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      {/* Starred messages drawer — opens from the thread header
                button. Per D4 the drawer carries a "This chat" / "All chats"
                toggle; cross-thread is BE-deferred. */}
      <StarredMessagesPanel
        open={starredPanelOpen}
        onClose={() => setStarredPanelOpen(false)}
        threadId={whatsAppChatMetaData?.thread?.id}
      />

      {/* Track C / F8 — Message Info drawer. */}
      <MessageInfoDrawer
        open={Boolean(messageInfoTarget)}
        onClose={() => setMessageInfoTarget(null)}
        threadId={messageInfoTarget?.threadId ?? null}
        messageId={messageInfoTarget?.messageId ?? null}
      />

      {/* Track D / F3 — Forward picker. Single-target per D11. */}
      <ForwardPickerDialog
        open={forwardPicker.open}
        count={forwardPicker.ids.length}
        currentThreadId={whatsAppChatMetaData?.thread?.id}
        onClose={closeForwardPicker}
        onConfirm={confirmForward}
      />

      {/* Service-window-closed dialog. Surfaces the backend's HTTP 409
                in a sticky surface (not a toast) because the operator needs
                the "switch to template" CTA without losing the typed draft. */}
      <Dialog
        open={Boolean(serviceWindowClosedDialog)}
        onClose={() => dispatch(clearServiceWindowClosedDialog())}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 16 }}>
          <Icon icon="mdi:clock-alert-outline" width={22} height={22} style={{ color: COLORS.warningDark }} />
          24-hour window closed
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: COLORS.textBody, mb: 1.5 }}>
            {serviceWindowClosedDialog?.error ||
              "Free-form WhatsApp messages can only be delivered within 24 hours of the contact's last inbound message."}
          </Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.textSecondary }}>
            Send an approved template instead — Meta will deliver it regardless of window state.
          </Typography>
          {serviceWindowClosedDialog?.draft && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 1,
                backgroundColor: COLORS.backgroundLight,
                border: `1px solid ${COLORS.dividerLight}`,
                fontSize: 12,
                color: COLORS.textPrimary,
                whiteSpace: 'pre-wrap',
                maxHeight: 96,
                overflow: 'auto',
              }}
            >
              <Typography sx={{ fontSize: 11, color: COLORS.textSecondary, mb: 0.5 }}>
                Your draft (preserved):
              </Typography>
              {serviceWindowClosedDialog.draft}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => dispatch(clearServiceWindowClosedDialog())}
            size="small"
            sx={{ textTransform: 'none', color: COLORS.textSecondary }}
          >
            Dismiss
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              dispatch(clearServiceWindowClosedDialog());
              setTemplateModalOpen(true);
            }}
            sx={{
              textTransform: 'none',
              backgroundColor: COLORS.primary,
              '&:hover': { backgroundColor: COLORS.primaryDark },
            }}
          >
            Switch to template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ChatPage.propTypes = {
  onToggleDetails: PropTypes.func,
  detailsOpen: PropTypes.bool,
  embedded: PropTypes.bool,
  onOpenBooking: PropTypes.func,
  onSetChatMessage: PropTypes.func,
  onOpenResolve: PropTypes.func,
  hideResolve: PropTypes.bool,
  onMessageSent: PropTypes.func,
  isMobile: PropTypes.bool,
  isCompact: PropTypes.bool,
  onBack: PropTypes.func,
  onToggleFullscreen: PropTypes.func,
  isFullscreen: PropTypes.bool,
  onToggleFocusMode: PropTypes.func,
  isFocusMode: PropTypes.bool,
};

ChatPage.defaultProps = {
  onToggleDetails: () => {},
  detailsOpen: false,
  embedded: false,
  onOpenBooking: undefined,
  onSetChatMessage: undefined,
  onOpenResolve: undefined,
  hideResolve: false,
  onMessageSent: undefined,
  isMobile: false,
  isCompact: false,
  onBack: undefined,
  onToggleFullscreen: undefined,
  isFullscreen: false,
  onToggleFocusMode: undefined,
  isFocusMode: false,
};

export default ChatPage;
